/* review-workspace.jsx — live review shell composed over the HTML-backed
   workspace core and panels. Ported from the be2 handoff, with certified-artifact
   decisions (D8), draft-only edits, and live review artifacts. */

import '../lib/review-comments.js';
import { revisionQaKey, revisionQaView, revisionQaPresentation, revisionQaNeedsRun, revisionCertifyAllowed, reviewBlockCounts, fetchRevisionQa, projectionDiagnostic } from '../lib/review-qa.js';
import { useCurrentQualificationReview, currentVerdictReady, verdictError, VERDICT_REQUIRED_COPY } from '../lib/review-verdict.js';
import './review-workspace-qa.jsx';
import { projectManualFields } from '../../../shared/editor-fields.js';
import { espOperationValid } from '../lib/review-esp.js';
import { EspRetainedSourceDiff } from './review-workspace-esp-source-diff.jsx';
import { chaseAcceptLabel } from '../lib/review-chase-ui.js';
import { RwVariantIdentity, RwRevisionDetails, RwWorkspaceNotices, reviewPreSendActions, reviewAnchorLabel } from './review-workspace-presentation.jsx';
import { reviewDeliveryReady } from '../lib/review-delivery.js';
import { ReviewDeliveryControl } from './review-delivery-control.jsx';

const RW_LAYOUT_KEY = 'goose-review-layout';
const RW_LAYOUT_DEFAULT = { rail: 258, ctx: 404, hideRail: false, hideCtx: false, focus: false, forceCtx: false };
const RW_LAYOUT = { MID_MIN: 440, RAIL_MIN: 72, RAIL_MINI: 84, RAIL_MAX: 460, CTX_MIN: 300, CTX_MAX: 720, H: 14, STRIP: 60 };
const rwClamp = (value, low, high) => Math.max(low, Math.min(high, value));

function rwApiErrorCode(error) {
  return error && error.data && error.data.code || error && error.code || null;
}

function rwApprovalBlockReason({ submitting, sessionPending, amendmentRunning, draftDirty, replayPending, hasSession, replayStatus, blockWhy }) {
  if (submitting) return 'Saving the review decision…';
  if (sessionPending) return 'Loading the exact revision…';
  if (amendmentRunning) return 'Wait for the current amendment and QA to finish.';
  if (draftDirty) return 'Edits pending certification — apply changes and rerun QA first.';
  if (replayPending) return 'This revision is not yet re-verified — rerun QA before approval.';
  if (hasSession && replayStatus !== 'exact') return 'This revision has no exact QA replay — reload or rerun QA before approval.';
  return blockWhy || null;
}
// RW-11 (RWB-1 FE): exact replay blockers remain shared with the submission handler.

function RwReviewDecisions({ canReview, readOnlyReason, dirty, approvalWhy, submitting,
  notesOpen, onRequestChanges, onReject, onApprove }) {
  return <div className="w-bar-decisions" role="group" aria-label="Review decisions">
    {canReview ? <React.Fragment>
      <button className="btn btn-ghost" disabled={!!submitting} onClick={onRequestChanges} data-action="request-changes"><BI.retry size={15} /> {submitting === 'request_changes' ? 'Saving…' : notesOpen ? 'Submit notes' : 'Request changes'}</button>
      <button className="btn btn-danger" disabled={!!submitting} onClick={onReject} data-action="reject"><BI.x size={15} /> {submitting === 'reject' ? 'Saving…' : 'Reject'}</button>
      <button className={dirty ? 'btn btn-ghost' : 'btn btn-primary btn-lg'} disabled={!!approvalWhy} title={approvalWhy || 'Cleared for takeoff.'} onClick={onApprove} data-action="approve"><BI.check size={16} /> {dirty ? 'Approve' : submitting === 'approve' ? 'Approving…' : 'Approve & deliver'}</button>
    </React.Fragment> : readOnlyReason
      ? <span className="muted row gap-6" style={{ fontSize: 12.5 }} data-review-readonly="1"><BI.warn size={15} /> {readOnlyReason}</span>
      : <span className="w-bar-note" data-review-reason="no-seat">Approval needs a reviewer seat — ask a reviewer to approve this revision.</span>}
  </div>;
}
// The editor-session 409 projection_drift body names WHY — retain the named reason.
function rwDriftCopy(detail) {
  const d = detail || {}, list = (a) => Array.isArray(a) && a.length ? a.join(', ') : null;
  switch (d.reason) {
    case 'anchor_duplicate': return 'an authored anchor appears twice' + (d.id ? ' (' + d.id + ')' : '');
    case 'anchor_nested': return 'an authored anchor lies inside another' + (d.inner ? ' (' + d.inner + ' inside ' + d.outer + ')' : '');
    case 'anchor_unclosed': return 'an authored anchor never closes' + (d.id ? ' (' + d.id + ')' : '');
    case 'anchor_invalid_id': return 'an authored anchor id is invalid' + (d.id ? ' (' + d.id + ')' : '');
    case 'anchor_mixed_families': return 'data-sec and data-region anchors cannot coexist';
    case 'anchor_bijection': return 'anchors and contract regions do not match' + [list(d.missing_anchors) && 'missing ' + list(d.missing_anchors), list(d.extra_anchors) && 'extra ' + list(d.extra_anchors), list(d.hidden_anchored) && 'hidden ' + list(d.hidden_anchored)].filter(Boolean).map((s) => ' · ' + s).join('');
    case 'executor_receipt_drift': return "the build receipt's executor version does not match the artifact";
    case 'anchor_marker_disagree': return 'the authored anchor and the SABRE markers disagree on the region span' + (d.id ? ' (' + d.id + ')' : '');
    default: return d.reason ? String(d.reason) : null;
  }
}

function rwReturnedUrl(url) {
  if (typeof url !== 'string' || !url) return url;
  if (/^(?:[a-z][a-z0-9+.-]*:|\/\/)/i.test(url)) return url;
  const base = window.GooseAPI && window.GooseAPI.base || '';
  return base && url.startsWith('/') ? base.replace(/\/$/, '') + url : url;
}

function rwResolveSessionUrls(session) {
  const artifacts = session && session.artifacts || {};
  const sourceRenders = artifacts.renders && typeof artifacts.renders === 'object' ? artifacts.renders : {};
  const renders = Object.fromEntries(Object.entries(sourceRenders).map(([width, url]) => [width, rwReturnedUrl(url)]));
  return {
    ...session,
    artifacts: {
      ...artifacts,
      html_url: rwReturnedUrl(artifacts.html_url),
      assets_base: rwReturnedUrl(artifacts.assets_base),
      renders,
      source_render: rwReturnedUrl(artifacts.source_render),
    },
  };
}

async function rwFetchReturnedText(url) {   // RW-05 integration (🐓): `url` is already base-resolved by rwResolveSessionUrls — never prefix again (was fetching /backend/backend/…)
  if (typeof url !== 'string' || !url) throw new Error('Revision HTML URL is unavailable.');
  const response = await fetch(url, { credentials: 'include' });
  const text = await response.text();
  if (!response.ok) {
    let data = null;
    try {data = text ? JSON.parse(text) : null;} catch (_) {}
    const error = new Error(data && data.error || 'HTTP ' + response.status);
    error.status = response.status;
    error.data = data;
    throw error;
  }
  return text;
}

function rwSessionAssetUrl(base, relative) {
  if (typeof base !== 'string' || !base) return null;
  const source = String(relative || '').replace(/^03_execution\//, '').replace(/^\.\//, '');
  if (!source.startsWith('images/')) return null;
  const suffix = source.slice('images/'.length).split('/').map(encodeURIComponent).join('/');
  return suffix ? base + suffix : base;
}

function rwOffsetPosition(text, offset) {
  const before = String(text || '').slice(0, Math.max(0, offset));
  const lines = before.split('\n');
  return { line: lines.length - 1, ch: lines[lines.length - 1].length };
}

function rwFindingTag(finding) {
  const value = String(finding && (finding.tag || finding.severity || finding.level || finding.status) || '').toLowerCase();
  if (/^(?:major|error|fail|blocked|critical|high|severe)$/.test(value)) return 'major';
  if (/^(?:minor|warn|warning|attention|medium|low|info)$/.test(value)) return 'minor';
  if (/^(?:pass|clean|ok|resolved)$/.test(value)) return 'pass';
  return null;
}

function rwFindingNote(finding) {
  return finding && (finding.note || finding.message || finding.detail || finding.summary) || null;
}

function rwProjectionModel(projection, html, findings) {
  const text = String(html || '');
  const topFindings = Array.isArray(findings) ? findings : [];
  const claimed = new Set();
  const blocks = (Array.isArray(projection && projection.blocks) ? projection.blocks : []).map((source, order) => {
    const id = String(source.block_id || source.id || 'block-' + (order + 1));
    const rawRange = source.source_range || source.range || {};
    const start = Math.max(0, Math.min(text.length, Number(rawRange.start) || 0));
    const endValue = Number(rawRange.end);
    const end = Math.max(start, Math.min(text.length, Number.isFinite(endValue) ? endValue : start));
    const startPos = rwOffsetPosition(text, start);
    const endPos = rwOffsetPosition(text, end);
    const ownFindings = Array.isArray(source.findings) ? source.findings : [];
    const boundFindings = topFindings.filter((finding, index) => {
      const owner = finding && (finding.block_id || finding.region_id || finding.blockId);
      if (owner == null || String(owner) !== id) return false;
      claimed.add(index);
      return true;
    });
    const diagnostic = projectionDiagnostic(source, [...ownFindings, ...boundFindings]);
    // The artifact projection remains exact. Optional draft controls are derived
    // separately and only for regions the server admitted to Manual editing.
    const code = text.slice(start, end);
    const controls = source.fieldProjection === 'artifact'
      ? Object.fromEntries(Object.entries(projectManualFields(code)).filter(([, field]) => field.certainty === 'edit_controls')) : {};
    const fields = { ...(source.fields && typeof source.fields === 'object' ? source.fields : {}), ...controls };
    const props = Object.fromEntries(Object.entries(fields).map(([key, value]) => [
      key,
      value && typeof value === 'object' && Object.prototype.hasOwnProperty.call(value, 'value') ? value.value : value,
    ]));
    // Stored projection descriptions never replace the live file being edited.
    const reviewState = source.review_state || 'clean';
    return {
      ...source,
      id,
      anchor_attr: ['data-region', 'data-sec'].includes(source.anchor_attr) ? source.anchor_attr
        : /^\[(data-region|data-sec)\s*=/.exec(source.dom_locator?.authored || '')?.[1],
      label: source.label || id,
      code,
      qa: diagnostic.qa,
      note: diagnostic.note,
      p: props,
      custom: !!source.custom,
      ico: window.GooseReviewBlocks.iconFor(source.label || id, code),
      identity: source.identity || (projection.identity === 'authored' ? 'authored' : 'derived'),
      elKey: source.elKey || id,
      range: { start, end, startLine: startPos.line, startCh: startPos.ch, endLine: endPos.line, endCh: endPos.ch },
      fields,
      review_state: reviewState,
    };
  });
  return {
    ok: blocks.length > 0,
    reason: blocks.length ? null : 'no-bands',
    identity: projection.identity || 'none',
    blocks,
    bands: blocks.map((block) => ({ sec: block.id, label: block.label, elKey: block.elKey, range: block.range, identity: block.identity })),
    unplaced: topFindings.filter((_, index) => !claimed.has(index)).map((finding) => ({
      ...finding,
      label: finding && (finding.label || finding.section || finding.title) || 'Build-level finding',
      tag: rwFindingTag(finding),
      note: rwFindingNote(finding),
    })),
  };
}

function rwFitLayout(inner, layout) {
  if (layout.focus) return { rail: 0, ctx: 0, showRail: false, showCtx: false, mini: false, parked: false, parkedRail: false };
  let rail = rwClamp(layout.rail, RW_LAYOUT.RAIL_MIN, RW_LAYOUT.RAIL_MAX);
  let ctx = rwClamp(layout.ctx, RW_LAYOUT.CTX_MIN, RW_LAYOUT.CTX_MAX);
  let showRail = !layout.hideRail, showCtx = !layout.hideCtx, parked = false, parkedRail = false;
  if (!inner || inner > 8000) return { rail, ctx, showRail, showCtx, mini: showRail && rail < 130, parked: false, parkedRail: false };
  const budget = () => inner - (showRail ? RW_LAYOUT.H : RW_LAYOUT.STRIP) - (showCtx ? RW_LAYOUT.H : RW_LAYOUT.STRIP);
  let mid = budget() - (showRail ? rail : 0) - (showCtx ? ctx : 0);
  if (mid < RW_LAYOUT.MID_MIN && showCtx) {ctx = Math.max(RW_LAYOUT.CTX_MIN, ctx - (RW_LAYOUT.MID_MIN - mid));mid = budget() - (showRail ? rail : 0) - ctx;}
  if (mid < RW_LAYOUT.MID_MIN && showRail) {rail = Math.max(RW_LAYOUT.RAIL_MINI, rail - (RW_LAYOUT.MID_MIN - mid));mid = budget() - rail - (showCtx ? ctx : 0);}
  // TE-153: layout.forceCtx is the reviewer asking for the panel back below inner 852. Honour it —
  // park the blocks rail and narrow the canvas instead — or the panel re-parks on the very render the
  // restore button triggered, and the verdict checklist (mounted only in this panel) stays unreachable.
  if (mid < RW_LAYOUT.MID_MIN && showCtx && !layout.forceCtx) {showCtx = false;parked = true;mid = budget() - (showRail ? rail : 0);}
  if (mid < RW_LAYOUT.MID_MIN && showRail) {showRail = false;parkedRail = true;mid = budget() - (showCtx ? ctx : 0);}
  if (mid < 260 && showRail) rail = RW_LAYOUT.RAIL_MINI;
  return { rail, ctx, showRail, showCtx, mini: showRail && rail < 130, parked, parkedRail };
}

function rwUseLayout() {
  const [layout, setLayout] = React.useState(() => {
    try {
      const stored = { ...RW_LAYOUT_DEFAULT, ...(JSON.parse(localStorage.getItem(RW_LAYOUT_KEY)) || {}) };
      stored.rail = rwClamp(+stored.rail || RW_LAYOUT_DEFAULT.rail, RW_LAYOUT.RAIL_MIN, RW_LAYOUT.RAIL_MAX);
      stored.ctx = rwClamp(+stored.ctx || RW_LAYOUT_DEFAULT.ctx, RW_LAYOUT.CTX_MIN, RW_LAYOUT.CTX_MAX);
      return stored;
    } catch (_) {return { ...RW_LAYOUT_DEFAULT };}
  });
  const set = (patch) => setLayout((previous) => {
    const next = { ...previous, ...patch };
    try {localStorage.setItem(RW_LAYOUT_KEY, JSON.stringify(next));} catch (_) {}
    return next;
  });
  return [layout, set];
}

function RwHandle({ onDrag, onReset, side }) {
  const [active, setActive] = React.useState(false);
  const down = (event) => {
    event.preventDefault();
    setActive(true);
    const start = event.clientX;
    const move = (next) => onDrag(next.clientX - start);
    const up = () => {
      setActive(false);
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      document.body.style.cursor = '';
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    document.body.style.cursor = 'col-resize';
  };
  return <div className={'w-handle' + (active ? ' on' : '')} onPointerDown={down} onDoubleClick={onReset}
    title={'Drag to resize the ' + side + ' panel · double-click to reset'}><i></i></div>;
}

function RwRail({ B, patchIds, mini, width, onHide, htmlState, unboundAnchors = 0, unmappedRegions = 0, shellFinding = null }) {
  const anchored = B.blocks.filter((block) => block.identity === 'authored').length;
  const anchorKinds = [...new Set(B.blocks.filter(block => block.identity === 'authored').map(block => block.anchor_attr || 'data-region'))];
  const emptyCopy = htmlState === 'loading'
    ? 'Loading build…'
    : htmlState === 'missing'
      ? 'index.html not materialized for this lane'
      : 'No editable regions materialized for this lane.';
  return (
    <aside className={'w-panel w-side w-rail' + (mini ? ' mini' : '')} style={{ width }} data-blocks-source={B.source}>
      <div className="w-panel-h">
        <BI.layers size={16} style={{ color: 'var(--brand-orange)' }} />
        {!mini && <h2>Blocks</h2>}
        {!mini && B.identity === 'derived' && <span className="w-tag identity" title="Derived sections are a display overlay, not artifact authority">derived</span>}
        {!mini && <span className="w-grow" style={{ flex: 1 }}></span>}
        {!mini && <span className="w-count">{B.blocks.length}</span>}
        {!mini && <button className="w-tab-x" onClick={onHide} title="Hide the blocks panel"><BI.x size={14} /></button>}
      </div>
      {!mini && (B.identity === 'authored' || B.identity === 'mixed') && <div className="w-bar-note" data-blocks-identity={B.identity} style={{ padding: '8px 14px', fontSize: 12 }}>
        {B.identity === 'authored' ? B.blocks.length + ' regions · builder-anchored (' + anchorKinds.join(', ') + ')' : anchored + ' anchored · ' + (B.blocks.length - anchored) + ' derived overlay'}
      </div>}
      {!mini && !!unboundAnchors && <div role="note" className="w-bar-note" data-projection-unbound-anchors={unboundAnchors} style={{ padding: '7px 14px', color: 'var(--cond)', background: 'var(--cond-bg)', fontSize: 12 }}>{unboundAnchors} anchor{unboundAnchors === 1 ? '' : 's'} without a contract region</div>}
      {!mini && !!unmappedRegions && <div role="note" className="w-bar-note" data-projection-unmapped-regions={unmappedRegions} style={{ padding: '7px 14px', color: 'var(--cond)', background: 'var(--cond-bg)', fontSize: 12 }}>{unmappedRegions} contract region{unmappedRegions === 1 ? '' : 's'} without an anchor</div>}
      <div className="w-scroll">
        <div className="w-blocks">
          {B.blocks.map((block) => {
            const BlockIcon = BI[block.ico] || BI.layers;
            return (
              <button key={block.id} className={'w-block' + (block.id === B.sel ? ' sel' : '')} data-block-id={block.id} data-block-identity={block.identity} onClick={() => B.setSel(block.id)} title={block.label + (block.note ? ' — ' + block.note : '') + (block.identity === 'authored' ? ' · ' + (block.anchor_attr || 'data-region') + '="' + block.id + '"' : '')}>
                <span className="w-b-ico"><BlockIcon size={15} /></span>
                <span className="w-b-n">{block.label}</span>
                <span className="w-b-tags">
                  {block.identity === 'derived' && <span className="w-tag identity" title="Derived section — display overlay">derived</span>}   {/* RW-10 pilot: its own class — `.w-tag.edit` is the edited marker the e2e selects */}
                  {patchIds.has(block.id) && <span className="w-tag patch">patch</span>}
                  {!patchIds.has(block.id) && B.touched.has(block.id) && <span className="w-tag edit">edited</span>}
                  <i className={'w-dot ' + (block.qa || 'ungraded')} aria-label={block.qa === 'major' ? 'Fail finding' : block.qa === 'minor' ? 'Minor finding' : block.qa === 'pass' ? 'Pass' : 'Ungraded'}></i>
                </span>
              </button>
            );
          })}
          {!B.blocks.length && !mini && (shellFinding
            ? <div className="w-bar-note" data-review-shell="unrecognized" style={{ padding: '12px 8px', lineHeight: 1.55 }}><b>shell not recognized — no regions</b>{rwFindingNote(shellFinding) && <span style={{ display: 'block', marginTop: 5 }}>{rwFindingNote(shellFinding)}</span>}</div>
            : <div className="w-bar-note" data-review-html={htmlState} style={{ padding: '12px 8px', lineHeight: 1.55 }}>{emptyCopy}</div>)}
        </div>
      </div>
      {!mini && B.identity === 'derived' && <div className="w-foot"><b>derived sections</b> — display overlay</div>}
    </aside>
  );
}

function RwViewport({ vp, setVp }) {
  const width = vp.mode === 'desktop' ? vp.desktop : vp.mobile;
  const presets = vp.mode === 'desktop' ? [600, 650, 750] : [320, 375, 414];
  const set = (value) => setVp((previous) => ({ ...previous, [previous.mode]: Math.max(200, Math.min(1400, value || 0)) }));
  const [draft, setDraft] = React.useState(null);
  const shown = draft != null ? draft : String(width);
  const commit = (raw) => {
    const next = parseInt(String(raw).replace(/[^\d]/g, ''), 10);
    setDraft(null);
    if (Number.isFinite(next)) set(next);
  };
  const nudge = (delta) => {setDraft(null);set(width + delta);};
  return (
    <React.Fragment>
      <div className="w-seg">
        <button className={vp.mode === 'desktop' ? 'on' : ''} onClick={() => setVp((previous) => ({ ...previous, mode: 'desktop' }))} title="Desktop viewport"><BI.desktop size={13} /> <span className="w-vp-lbl">Desktop</span></button>
        <button className={vp.mode === 'mobile' ? 'on' : ''} onClick={() => setVp((previous) => ({ ...previous, mode: 'mobile' }))} title="Mobile viewport"><BI.mobile size={13} /> <span className="w-vp-lbl">Mobile</span></button>
      </div>
      <div className="w-vp">
        <input className="w-in w-vp-in" type="text" inputMode="numeric" value={shown}
          onChange={(event) => setDraft(event.target.value.replace(/[^\d]/g, '').slice(0, 4))}
          onFocus={(event) => event.target.select()}
          onBlur={(event) => commit(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {commit(event.currentTarget.value);event.currentTarget.blur();}
            else if (event.key === 'Escape') {setDraft(null);event.currentTarget.blur();}
            else if (event.key === 'ArrowUp') {event.preventDefault();nudge(event.shiftKey ? 10 : 1);}
            else if (event.key === 'ArrowDown') {event.preventDefault();nudge(event.shiftKey ? -10 : -1);}
          }}
          title="Custom viewport width · 200–1400px · ↵ to apply, ↑↓ to nudge" />
        <span className="w-vp-u">px</span>
        <div className="w-vp-pre">{presets.map((preset) => <button key={preset} className={width === preset ? 'on' : ''} onClick={() => {setDraft(null);set(preset);}}>{preset}</button>)}</div>
      </div>
    </React.Fragment>
  );
}

function rwUseMeasured() {
  const [element, setElement] = React.useState(null);
  const [width, setWidth] = React.useState(9999);
  React.useLayoutEffect(() => {
    if (!element) return;
    const measure = () => setWidth(element.clientWidth);
    measure();
    if (window.ResizeObserver) {const observer = new ResizeObserver(measure);observer.observe(element);return () => observer.disconnect();}
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [element]);
  return [setElement, width];
}

function rwUseWidthOf(ref) {
  const [width, setWidth] = React.useState(9999);
  React.useLayoutEffect(() => {
    const element = ref.current;if (!element) return;
    const measure = () => setWidth(element.clientWidth);
    measure();
    if (window.ResizeObserver) {const observer = new ResizeObserver(measure);observer.observe(element);return () => observer.disconnect();}
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);
  return width;
}

function RwStagedBar({ B, chase, preview, setPreview, midWidth, onViewDiff }) {
  const [open, setOpen] = React.useState(false);
  const [barRef, measuredWidth] = rwUseMeasured();
  const width = midWidth ? midWidth - 54 : measuredWidth;
  const tight = width < 800, tighter = width < 600;
  const rows = chase.queue;
  const count = rows.length;
  const currentIndex = Math.max(0, rows.findIndex(row => row.pid === chase.selectedPatch?.pid && row.blockId === chase.selectedPatch?.blockId));
  const current = rows[currentIndex];
  if (!count || !current) return null;
  const proposalIds = [...new Set(rows.map(patch => patch.pid))];
  const currentPatches = rows.filter(patch => patch.pid === current.pid);
  const acceptAllBlocked = !!chase.phase || chase.disabled || chase.acceptBlocked || rows.some(patch => patch.stale)
    || (proposalIds.length > 1 && (!chase.proposalSet?.enabled || !chase.acceptAll));
  const acceptAll = () => proposalIds.length === 1 ? chase.accept(current.pid) : chase.acceptAll?.();
  const openRegion = patch => {
    chase.selectPatch(patch);
    if (patch.blockId !== 'document') B.setSel(patch.blockId);
    onViewDiff(patch.blockId);
  };
  const step = (delta) => {
    const next = (currentIndex + delta + count) % count;
    openRegion(rows[next]);
  };
  return (
    <div className="w-sheet">
      <div className={'w-sheet-h' + (tight ? ' tight' : '') + (tighter ? ' tighter' : '')} ref={barRef}>
        <span className="w-spark"><BI.jet size={12} /></span>
        <span className="w-sheet-t">Chase staged</span>
        <span className="w-pill-ok">{count}</span>
        <span className="w-step">
          <button onClick={() => step(-1)} title="Previous patch"><BI.chevron size={12} style={{ transform: 'rotate(180deg)' }} /></button>
          <span className="w-step-n" data-chase-patch-position>{currentIndex + 1}/{count}</span>
          <button onClick={() => step(1)} title="Next patch"><BI.chevron size={12} /></button>
          <button className="w-step-l" title={'Open the diff for ' + current.label.toLowerCase()} onClick={() => openRegion(current)}>{current.label}</button>
        </span>
        {chase.canAccept && <button className="btn btn-primary btn-sm w-nowrap" disabled={!!chase.phase || chase.disabled || chase.acceptBlocked || current.stale} onClick={() => chase.accept(current.pid)} aria-label={chaseAcceptLabel(currentPatches)} title={chaseAcceptLabel(currentPatches)}><BI.check size={13} /> <span className="w-sh-lbl">Accept</span></button>}
        <span className="w-grow" style={{ flex: 1, minWidth: 0 }}></span>
        <button className={'w-toggle' + (preview ? ' on' : '')} disabled={chase.acceptBlocked || current.stale} onClick={() => setPreview(!preview)} title={chase.proposalSet?.enabled ? 'Preview all compatible proposals in the full email; nothing is saved' : 'Preview only the selected proposal in the full email'}><i>{preview && <BI.check size={11} />}</i> <span className="w-sh-lbl">{chase.proposalSet?.enabled && count > 1 ? 'Preview all' : 'Preview'}</span></button>
        <button className="btn btn-ghost btn-sm w-nowrap" onClick={() => setOpen((value) => !value)} title={open ? 'Hide the patch list' : 'List all patches'}>{tighter ? <BI.chevron size={13} style={{ transform: open ? 'rotate(-90deg)' : 'rotate(90deg)' }} /> : open ? 'Hide list' : 'List all'}</button>
        {chase.canAccept && <button className="btn btn-primary btn-sm w-nowrap" data-chase-accept-all disabled={acceptAllBlocked} onClick={acceptAll} title={proposalIds.length > 1 ? chase.proposalSet?.reason || 'Accept every staged patch' : chaseAcceptLabel(currentPatches)}><BI.check size={13} /><span className="w-sh-lbl">Accept all</span>{tighter && <span className="w-sh-n">{count}</span>}</button>}
      </div>
      {proposalIds.length > 1 && !chase.proposalSet?.enabled && <div className="w-bar-note" data-chase-set-conflict>{chase.proposalSet?.reason || 'These proposals cannot be combined safely. Review and accept one proposal at a time.'}</div>}
      {open && <div className="w-sheet-b">
        {rows.map(patch => <div key={patch.pid+':'+patch.blockId} data-chase-canvas-proposal={patch.pid} data-chase-canvas-region={patch.blockId} className={'w-patch' + (patch.pid === current.pid && patch.blockId === current.blockId ? ' sel' : '')}>
          <div className="w-patch-r">
            <button className="w-patch-r" style={{ padding: 0, gap: 11, flex: 1, minWidth: 0 }} onClick={() => {chase.selectPatch(patch);onViewDiff(patch.blockId);}} title="Open this diff in the canvas">
              <BI.code size={13} style={{ color: 'var(--muted)', flexShrink: 0 }} />
              <span className="w-patch-n">{patch.label}</span>
              <span className="w-patch-w">{patch.why}</span>
            </button>
            <button className="btn btn-quiet btn-sm" onClick={() => chase.discard(patch.pid)}><BI.x size={13} /></button>
            {chase.canAccept && <button className="btn btn-primary btn-sm" aria-label={chaseAcceptLabel(rows.filter(item => item.pid === patch.pid))} title={chaseAcceptLabel(rows.filter(item => item.pid === patch.pid))} disabled={!!chase.phase || chase.disabled || chase.acceptBlocked || patch.stale} onClick={() => chase.accept(patch.pid)}><BI.check size={13} /> Accept</button>}
          </div>
        </div>)}
      </div>}
    </div>
  );
}

function rwCodeDiffPatchFor(selected, queue, block) {
  if (selected && (selected.blockId === 'document' || selected.blockId === block?.id)) return selected;
  return (queue || []).find(patch => patch.blockId === block?.id) || null;
}

function RwCodeDiffView({ B, chase, pad }) {
  const block = B.cur;
  const selected = chase.selectedPatch;
  const patch = rwCodeDiffPatchFor(selected, chase.queue, block);
  if (!block && !patch) return <div className="w-cd"><div className="w-cd-b"><div className="w-bar-note" style={{ padding: 16 }}>No editable region selected.</div></div></div>;
  const lines = patch ? rwDiff(patch.before, patch.after, 500) : [];
  return (
    <div className="w-cd" style={{ marginBottom: 14 + (pad || 0) }}>
      <div className="w-cd-h">
        <BI.code size={15} style={{ color: 'var(--brand-orange)' }} />
        <b>{patch ? patch.label : block.label}</b>
        <code>{patch?.blockId === 'document' ? 'document scope' : reviewAnchorLabel(patch ? B.blocks.find(item => item.id === patch.blockId) : block)}</code>
        {patch ? <span className="w-pill-ok">{patch.stale ? 'stale proposal · reload/re-request' : 'staged patch'}</span> : <span className="w-meta">no staged patch — current source</span>}
        <span className="w-grow"></span>
        {patch && <button className="btn btn-quiet btn-sm" onClick={() => chase.discard(patch.pid)}><BI.x size={13} /> Discard</button>}
        {patch && chase.canAccept && <button className="btn btn-primary btn-sm" aria-label={chaseAcceptLabel(chase.queue.filter(item => item.pid === patch.pid))} title={chaseAcceptLabel(chase.queue.filter(item => item.pid === patch.pid))} disabled={!!chase.phase || chase.disabled || chase.acceptBlocked || patch.stale} onClick={() => chase.accept(patch.pid)}><BI.check size={13} /> {chase.queue.filter(item => item.pid === patch.pid).length > 1 ? 'Accept proposal' : 'Accept patch'}</button>}
      </div>
      <div className="w-cd-b">
        {patch ? lines.map((line, index) => <div key={index} className={'w-dl ' + line.t}><i>{line.t === 'add' ? '+' : line.t === 'del' ? '−' : ''}</i>{line.s}</div>) : <pre className="w-cd-pre">{block.code}</pre>}
      </div>
      {patch && <div className="w-cd-legend"><span><i className="del"></i>current build</span><span><i className="add"></i>Chase's patch</span><span className="w-grow" style={{ flex: 1 }}></span><span>{patch.why}</span></div>}
    </div>
  );
}

function RwSessionRenderSlot({ device, c, id, label, bare, url }) {
  const mobile = device === 'mobile';
  const [imgOk, setImgOk] = React.useState(false), [imgFailed, setImgFailed] = React.useState(false);
  React.useEffect(() => {setImgOk(false);setImgFailed(false);}, [url, mobile]);
  const open = (event) => {
    if (url && window.openRenderLightbox) {
      window.openRenderLightbox(event.currentTarget.querySelector('.rs-frame'), label + ' · ' + (mobile ? '375px mobile' : 'desktop ' + (c.width || 600) + 'px'));
    }
  };
  return (
    <figure className="rs" onDoubleClick={open} title={url ? 'Double-click to enlarge' : 'Not materialized'} data-render-slot={id} data-session-render={url ? 'ready' : 'missing'}>
      <div className={'rs-frame ' + (mobile ? 'rs-mobile' : 'rs-desktop')}>
        {url
          ? <img src={url} className="rs-img" alt={label} style={{ display: imgOk ? 'block' : 'none', width: '100%', height: 'auto' }} onLoad={() => setImgOk(true)} onError={() => {setImgOk(false);setImgFailed(true);}} />
          : <img data-render={id} className="rs-img" alt={label} />}
        <div className="rs-render email-mock" style={{ display: url && imgOk ? 'none' : undefined }}>
          <div className="w-bar-note" style={{ padding: 16, textAlign: 'center' }}>{imgFailed ? 'This retained render could not be loaded. Reload or rerun QA; no substitute render is shown.' : url ? 'Loading exact revision render…' : 'Not materialized at this width for this revision'}</div>
        </div>
      </div>
      {!bare && <figcaption className="rs-cap"><BI.image size={11} /> {label}<span className="rs-hint">{url ? '⤢ dbl-click' : 'unavailable'}</span></figcaption>}
    </figure>
  );
}

function RwVisualDiffFallback({ rwTabProps, setVp, triesRef, builtReady, setBuiltReady }) {
  const { c, isLive, job, vp } = rwTabProps;
  const [diffMode, setDiffMode] = React.useState('side');
  const [blend, setBlend] = React.useState(55);
  const [overlayMode, setOverlayMode] = React.useState('fade');
  const device = vp.mode;
  const setDevice = (mode) => setVp((previous) => ({ ...previous, mode }));
  const slot = (props) => {
    if (rwTabProps.sessionArtifacts) {
      const url = props.id === 'render-source'
        ? rwTabProps.sessionArtifacts.source_render
        : rwTabProps.sessionArtifacts.renders && rwTabProps.sessionArtifacts.renders[device === 'mobile' ? '375' : '600'];
      return <RwSessionRenderSlot device={device} c={c} url={url || null} {...props} />;
    }
    return <WsRenderSlot device={device} c={c} isLive={isLive} job={job} triesRef={triesRef} builtReady={builtReady} setBuiltReady={setBuiltReady} {...props} />;
  };
  return (
    <div className="w-field" style={{ paddingBottom: 34 }}>
      <div className="card card-pad" style={{ width: '100%', maxWidth: 1180 }}>
        <div className="sec-head">
          <span className="sec-title">Visual diff</span>
          <div className="row gap-12">
            <div className="tabs"><span className={'tab' + (device === 'desktop' ? ' active' : '')} onClick={() => setDevice('desktop')}>Desktop</span><span className={'tab' + (device === 'mobile' ? ' active' : '')} onClick={() => setDevice('mobile')}>Mobile</span></div>
            <div className="tabs"><span className={'tab' + (diffMode === 'side' ? ' active' : '')} onClick={() => setDiffMode('side')}>Side-by-side</span><span className={'tab' + (diffMode === 'overlay' ? ' active' : '')} onClick={() => setDiffMode('overlay')}>Overlay</span></div>
          </div>
        </div>
        {diffMode === 'side' ? <div className="diff-wrap">
          <div className="diff-col"><div className="diff-head"><span className="dot" style={{ width: 8, height: 8, borderRadius: 4, background: 'var(--running)' }}></span> Built render</div><div className="diff-canvas">{slot({ tint: true, label: 'Built render', id: 'render-built' })}</div></div>
          <div className="diff-col"><div className="diff-head"><span className="dot" style={{ width: 8, height: 8, borderRadius: 4, background: 'var(--faint)' }}></span> Source design</div><div className="diff-canvas">{slot({ tint: false, label: 'Source design', id: 'render-source' })}</div></div>
        </div> : <div className="diff-ov">
          <div className="diff-canvas">
            <div className="diff-stack" style={{ alignItems: overlayMode === 'wipe' ? 'start' : 'center', justifyItems: 'center', position: 'relative' }}>
              {slot({ tint: false, label: 'Source design', id: 'render-source', bare: true })}
              <div className="diff-top" style={overlayMode === 'wipe' ? { clipPath: 'inset(0 0 0 ' + (100 - blend) + '%)' } : { opacity: blend / 100 }}>{slot({ tint: true, label: 'Built render', id: 'render-built', bare: true })}</div>
              {overlayMode === 'wipe' && <div aria-hidden="true" style={{ position: 'absolute', top: 0, bottom: 0, left: (100 - blend) + '%', width: 2, marginLeft: -1, background: 'var(--brand-orange)', opacity: 0.85, zIndex: 3, pointerEvents: 'none' }}></div>}
            </div>
          </div>
          <div className="tabs" style={{ alignSelf: 'center' }} data-overlay-mode={overlayMode}><span className={'tab' + (overlayMode === 'fade' ? ' active' : '')} onClick={() => setOverlayMode('fade')}>Fade</span><span className={'tab' + (overlayMode === 'wipe' ? ' active' : '')} onClick={() => setOverlayMode('wipe')}>Wipe</span></div>
          <div className="diff-blend"><span className="row gap-6"><span className="dot" style={{ width: 7, height: 7, borderRadius: 4, background: 'var(--faint)' }}></span>Source</span><input className="fb-slider" type="range" min="0" max="100" value={blend} onChange={(event) => setBlend(parseInt(event.target.value, 10))} style={{ '--pct': blend + '%' }} /><span className="row gap-6">Built<span className="dot" style={{ width: 7, height: 7, borderRadius: 4, background: 'var(--running)' }}></span></span></div>
          <p className="muted" style={{ fontSize: 11.5, textAlign: 'center', margin: '4px 0 0' }}>{overlayMode === 'wipe' ? 'Drag the slider to wipe between the source design and the built render' : 'Drag to fade between the source design and the built render'} · double-click to enlarge</p>
        </div>}
      </div>
    </div>
  );
}

function RwAfterburnerFallback({ afterburnerStatus, onRunAfterburner }) {
  const card = afterburnerStatus === undefined
    ? { value: 'Checking Afterburner status…', tone: null, status: 'loading' }
    : afterburnerStatus === null
      ? { value: 'Not materialized for this lane', tone: null, status: 'not_materialized' }
      : reviewAfterburnerCard(afterburnerStatus);
  const viewer = window.RBAC && window.RBAC.viewer && window.RBAC.viewer();
  const allowed = viewer ? !!(window.RBAC.requireRole && window.RBAC.requireRole('afterburner')) : true;
  const canRun = allowed && (card.status === 'not_started' || card.status === 'idle');
  return (
    <div className="w-ab">
      <div className="w-ab-top"><span className="w-ab-flame"><BI.flame size={14} /></span><b>Afterburner</b><span className="w-grow"></span><span className={'w-abp ' + (card.tone === 'pass' ? 'pass' : card.tone === 'run' ? 'run' : card.tone === 'fail' ? 'fail' : card.tone === 'cond' ? 'cond' : 'ready')} data-afterburner-status={card.status}>{card.value}</span></div>
      <div className="w-ab-empty"><BI.flame size={24} /><b>{card.value}</b><span>Cross-client render QA is shown here when the lane materializes an Afterburner run.</span>{canRun && <button className="btn btn-primary" onClick={onRunAfterburner} data-afterburner-run="1">Run Afterburner</button>}</div>
    </div>
  );
}

function RwCanvas({ B, chase, html, view, setView, vp, setVp, zoom, setZoom, preview, setPreview, midWidth, onFull, onViewDiff,
  rwTabProps, artifactUrl, isolated, builtRenderTries, builtReady, setBuiltReady, htmlState }) {
  const block = B.cur;
  const CurrentIcon = block ? (BI[block.ico] || BI.layers) : BI.layers;
  const toolsRef = React.useRef(null);
  const measuredWidth = rwUseWidthOf(toolsRef);
  const toolsWidth = midWidth ? midWidth - 36 : measuredWidth;
  const width = vp.mode === 'desktop' ? vp.desktop : vp.mobile;
  const combined = chase.proposalSet?.enabled && chase.proposalSet.proposalIds?.length > 1;
  const selectedQueue = combined ? chase.proposalSet.patches : chase.queue.filter(p => p.pid === chase.selectedPatch?.pid);
  const patched = new Set(selectedQueue.map((patch) => {
    const block = B.blocks.find((entry) => entry.id === patch.blockId);
    return block && block.previewId || patch.blockId;
  }));
  const previewResult = preview && !chase.acceptBlocked
    ? combined ? window.GooseReviewBlocks.previewChaseProposalSet(html, B.index, chase.queue, chase.proposalSet)
      : window.GooseReviewBlocks.previewChaseProposal(html, B.index, chase.queue, chase.selectedPatch?.pid) : { html };
  const shownHtml = previewResult.html;
  const stagedPreview = shownHtml !== html;
  const [diffFull, setDiffFull] = React.useState(false);
  const pad = chase.queue.length ? 74 : 0;
  const [overflow, setOverflow] = React.useState(null);
  const [geometry, setGeometry] = React.useState(null);
  const measuredGeometry = geometry && geometry.html === shownHtml && geometry.viewportWidth === width
    && geometry.regionId === (B.previewSel || B.sel) ? geometry : null;
  const overflowBlocks = overflow && overflow.blocks && overflow.blocks.length ? overflow.blocks.map((id) => (B.blocks.find((entry) => entry.id === id) || {}).label || id) : [];
  const PluggedVisualDiff = window.RwVisualDiff;
  const RenderWidthEvidence = window.RwRenderWidthEvidence;
  const PluggedAfterburner = window.RwAfterburnerView;

  return (
    <main className="w-panel w-mid w-canvas">
      <div className={'w-tools' + (toolsWidth < 680 ? ' tight' : '') + (toolsWidth < 520 ? ' tighter' : '')} ref={toolsRef}>
        <div className="w-seg">
          <button className={view === 'render' ? 'on' : ''} onClick={() => setView('render')}>Render</button>
          <button className={view === 'diff' ? 'on' : ''} onClick={() => setView('diff')}>Visual diff</button>
          <button className={view === 'code' ? 'on' : ''} onClick={() => setView('code')}>Code diff{!!chase.queue.length && <span className="w-badge ok">{chase.queue.length}</span>}</button>
          {rwTabProps.espComparison && <button className={view === 'esp-source' ? 'on' : ''} data-esp-source-canvas-tab onClick={() => setView('esp-source')}>ESP source diff</button>}
          <button className={'ab' + (view === 'ab' ? ' on' : '')} onClick={() => setView('ab')} title="Afterburner · cross-client render QA"><BI.flame size={12} /> Afterburner</button>
        </div>
        {view === 'code' && block && rwTabProps.onFull && <button className="w-chip" onClick={onFull} title="Open the full-screen code editor"><BI.expand size={12} /> Full screen</button>}
        {view === 'diff' && PluggedVisualDiff && <button className="w-chip" data-action="diff-fullscreen" onClick={() => setDiffFull(true)} title="Open visual diff full screen"><BI.expand size={12} /> Full screen</button>}
        {view !== 'code' && view !== 'ab' && view !== 'esp-source' && <RwViewport vp={vp} setVp={setVp} />}
        {view === 'render' && <button className={'w-chip w-fitbtn' + (zoom === 'fit' ? ' on' : '')} onClick={() => setZoom(zoom === 'fit' ? 1 : 'fit')}
          title={zoom === 'fit' ? 'Zoom: scaled down to fit the canvas — the email is still ' + width + 'px wide. Click for true 100%.' : 'Zoom: true 100% — 1 screen pixel per email pixel, scroll to see it all. Click to fit the canvas.'}>{zoom === 'fit' ? 'Fit' : '100%'}</button>}
        <span className="w-grow" style={{ flex: 1 }}></span>
        {overflow && view === 'render' && <span className="w-flag" data-draft-warning="1" title={'Draft preview warning — viewport width not matched by the output. The output is ' + overflow.docW + 'px wide in a ' + overflow.w + 'px viewport' + (overflowBlocks.length ? ' — widest: ' + overflowBlocks.join(', ') : '') + '.'}><BI.warn size={13} /><b>Viewport width not matched by the output</b><span className="w-flag-d">{overflow.docW}px in {overflow.w}px · +{overflow.over}px{overflowBlocks.length ? ' · ' + overflowBlocks[0] : ''}</span></span>}
        {view === 'render' && RenderWidthEvidence && <RenderWidthEvidence {...rwTabProps} width={width} stagedPreview={stagedPreview} />}
        {view === 'esp-source' ? <span className="w-sel-read"><b>Retained ESP source</b><code>read-only</code></span> : <span className="w-sel-read"><CurrentIcon size={14} style={{ color: 'var(--muted)' }} /><b>{block ? block.label : htmlState === 'loading' ? 'Loading build…' : 'No region selected'}</b><code data-region-geometry={measuredGeometry ? 'measured' : 'unmeasured'}>{measuredGeometry ? Math.round(measuredGeometry.width) + '×' + Math.round(measuredGeometry.height) + 'px' : 'Viewport ' + width + 'px · region unmeasured'}</code></span>}
      </div>

      {view === 'render' ? <div className="w-field" style={{ paddingBottom: 34 + pad }}>
        {html ? <BeFrame html={shownHtml} sel={B.previewSel || B.sel} onSel={(id) => B.setSel(B.fromPreview ? B.fromPreview(id) : id)} width={width} zoom={zoom === 'fit' ? undefined : 1} fit={zoom === 'fit'} fitH={true} patched={preview ? patched : null} resizeKey={midWidth + ':' + pad} onOverflow={setOverflow} onGeometry={setGeometry} artifactUrl={artifactUrl} isolated={isolated} /> : <div className="w-bar-note">{htmlState === 'loading' ? 'Loading build…' : 'index.html not materialized for this lane'}</div>}
      </div> : view === 'esp-source' ? (rwTabProps.espComparison ? <EspRetainedSourceDiff
        pair={rwTabProps.espComparison.pair} expectedOperation={rwTabProps.espComparison.expected} jobId={rwTabProps.job.id}
        currentRevisionDigest={rwTabProps.editorSession?.revision_digest} draftDirty={rwTabProps.draftDirty}
        historical={rwTabProps.espComparisonHistorical} onClose={rwTabProps.onCloseEspDiff} />
        : <div className="w-bar-note" data-esp-source-unavailable>Open a retained conversion in the ESP tab to compare its exact source files.</div>)
        : view === 'code' ? <RwCodeDiffView B={B} chase={chase} pad={pad} /> : view === 'ab' ? (PluggedAfterburner ? <PluggedAfterburner {...rwTabProps} /> : <RwAfterburnerFallback afterburnerStatus={rwTabProps.afterburnerStatus} onRunAfterburner={rwTabProps.onRunAfterburner} />) : (PluggedVisualDiff ? <PluggedVisualDiff {...rwTabProps} liveHtml={shownHtml} stagedPreview={stagedPreview}
          patched={preview ? patched : null} midWidth={midWidth} artifactUrl={artifactUrl} isolated={isolated} full={diffFull} onCloseFull={() => setDiffFull(false)} /> : <RwVisualDiffFallback rwTabProps={rwTabProps} setVp={setVp} triesRef={builtRenderTries} builtReady={builtReady} setBuiltReady={setBuiltReady} />)}

      {view !== 'esp-source' && <RwStagedBar B={B} chase={chase} preview={preview} setPreview={setPreview} midWidth={midWidth} onViewDiff={(id) => {if (id !== 'document') B.setSel(id);setView('code');}} />}
    </main>
  );
}

function RwContextPanel({ B, chase, tab, setTab, onHide, width, threads, setThreads, rwTabProps, canInspect, canAmend,
  editBlockedCopy, qaSummary, imgRefAudit, textAccuracy, revisions, session, canRestore, onRevisionHead, commentFeed, commentFocus, onCommentFocused }) {
  const grades = reviewBlockCounts(B.blocks);
  const findings = grades.minor + grades.fail;
  const openComments = threads.filter((thread) => thread.open).length;
  const tabs = [
    ['inspector', BI.sliders, 'Inspector', null],
    ['review', BI.review, 'Review', findings ? { n: findings, ok: false } : null],
    ['chase', BI.jet, 'Chase', chase.queue.length ? { n: chase.queue.length, ok: true } : null],
    ['comments', BI.chat, 'Comments', openComments ? { n: openComments, ok: false } : null],
    ['esp', BI.tag, 'ESP', null],
    ['history', BI.clock, 'History', null],
  ];
  const PluggedReviewTab = window.RwSabreReviewTab;
  const PluggedEspTab = window.RwEspTab;
  return (
    <aside className="w-panel w-side" style={{ width }}>
      <div className="w-tabs">
        {tabs.map(([key, Icon, label, badge]) => <button key={key} className={tab === key ? 'on' : ''} onClick={() => setTab(key)} title={label + (badge ? ' · ' + badge.n : '')} aria-label={label}>
          <Icon size={16} />{badge && <span className={'w-badge' + (badge.ok ? ' ok' : '')}>{badge.n > 9 ? '9+' : badge.n}</span>}
        </button>)}
        <button className="w-tab-x" onClick={onHide} title="Hide this panel"><BI.x size={14} /></button>
      </div>
      {tab === 'inspector' && canInspect && B.cur && !B.cur.range && <div role="note" className="w-bar-note" style={{ padding: '8px 14px' }}>Apply the document changes or reload before editing this block — its source range changed</div>}
      {tab === 'inspector' && (canInspect
        ? <InspectorTab B={B} chase={chase} onViewDiff={rwTabProps.onViewDiff} onFull={rwTabProps.onFull} job={rwTabProps.job} session={session} canAmend={canAmend} />
        : <div className="w-scroll w-pad"><div className="w-callout info"><BI.warn size={15} /><span>{editBlockedCopy || 'Editing is available to builders, SMEs, and team leads.'}</span></div></div>)}
      {tab === 'review' && (PluggedReviewTab ? <PluggedReviewTab {...rwTabProps} /> : <ReviewTab
        job={rwTabProps.job} c={rwTabProps.c} isLive={rwTabProps.isLive} B={B} chase={chase}
        qaSummary={qaSummary} imgRefAudit={imgRefAudit} textAccuracy={textAccuracy}
        afterburnerStatus={rwTabProps.afterburnerStatus} receipt={rwTabProps.receipt}
        pre={rwTabProps.pre} setPre={rwTabProps.setPre} notesOpen={rwTabProps.notesOpen}
        notes={rwTabProps.notes} setNotes={rwTabProps.setNotes} onRunAfterburner={rwTabProps.onRunAfterburner}
        unplaced={rwTabProps.unplaced} revisionQa={rwTabProps.revisionQa}
      />)}
      {tab === 'chase' && <ChaseTab B={B} chase={chase} onViewDiff={rwTabProps.onViewDiff} job={rwTabProps.job} />}
      {tab === 'comments' && <CommentsTab key={rwTabProps.job && (rwTabProps.job.id || rwTabProps.job._id) || 'mock'} threads={threads} setThreads={setThreads} B={B} isLive={rwTabProps.isLive} job={rwTabProps.job}
        session={session} chase={chase} commentFeed={commentFeed} focusThreadId={commentFocus} onThreadFocused={onCommentFocused} onComposeChase={() => setTab('chase')}
        onViewProposal={id => {chase.viewRetainedProposal?.(id);setTab('chase');chase.refresh?.();}} />}
      {tab === 'esp' && (PluggedEspTab ? <PluggedEspTab {...rwTabProps} canCheck={canAmend} /> : <div className="w-scroll w-pad"><div className="w-callout info" data-esp-status="not-run"><BI.tag size={15} /><span><b>ESP checks not run.</b><br />The client-specific ESP review arrives in A2.</span></div></div>)}
      {tab === 'history' && <HistoryTab B={B} revisions={revisions} job={rwTabProps.job} session={session} canRestore={canRestore} onHead={onRevisionHead} />}
    </aside>
  );
}

// TE-153: the context column owns both branches — the panel and the strip that re-opens it — so the
// restore path is one testable unit. Restoring sets layout.forceCtx, which rwFitLayout honours at any
// width; before it, the strip patched hideCtx (already false when parked) and nothing happened.
function RwContextColumn({ fitted, layout, setLayout, setTab, onDrag, onReset, panel }) {
  if (layout.focus) return null;
  if (fitted.showCtx) return <React.Fragment><RwHandle side="context" onDrag={onDrag} onReset={onReset} />{panel}</React.Fragment>;
  const cost = fitted.showRail ? 'parks the blocks panel and narrows the canvas' : 'narrows the canvas';
  return <React.Fragment><span style={{ width: 14, flex: '0 0 14px' }}></span><div className="w-restore">{[['inspector', BI.sliders, 'Inspector'], ['review', BI.review, 'Review'], ['chase', BI.jet, 'Chase'], ['comments', BI.chat, 'Comments'], ['esp', BI.tag, 'ESP'], ['history', BI.clock, 'History']].map(([key, Icon, label]) => <button key={key} onClick={() => {setLayout({ hideCtx: false, forceCtx: true });setTab(key);}} title={fitted.parked ? label + ' — the window is too narrow for the panel and the canvas; opening it ' + cost : 'Show ' + label}><Icon size={15} /></button>)}<span className="w-restore-lbl">panel</span></div></React.Fragment>;
}

function RwFullCode({ B, chase, html, isLive, job, artifactUrl, isolated, onClose }) {
  const [scope, setScope] = React.useState('block');
  const block = B.cur;
  const editor = B.editor;
  const draftBlock = editor.blocks.find(entry => entry.id === block?.id);
  const blockValue = draftBlock?.code || '';
  const wholeValue = editor.html;
  const unlocated = editor.blocks.filter(entry => !entry.range);
  const boundaryWarning = unlocated.length ? unlocated.length + ' section' + (unlocated.length === 1 ? '' : 's') + ' cannot be located in this draft' : null;
  const [message, setMessage] = React.useState(null);
  const fileDirty = editor.dirty;
  const apply = () => {
    if (editor.apply()) setMessage('Code draft applied locally — rerun full-email QA before approval.');
  };
  React.useEffect(() => {
    const key = (event) => {
      if (event.key === 'Escape') onClose();
      if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) apply();
    };
    window.addEventListener('keydown', key);
    return () => window.removeEventListener('keydown', key);
  }, [editor, onClose]);
  const [split, setSplit] = React.useState(false);
  const lineMarks = React.useMemo(() => {
    try {
      return editor.blocks.filter((block) => block.range).map((block) => ({ id: block.id, from: block.range.startLine, to: block.range.endLine }));
    } catch (_) {return [];}
  }, [editor.blocks]);
  if (!block) return null;
  const dirty = fileDirty;
  const currentIndex = B.blocks.indexOf(block);
  const step = (delta) => B.setSel(B.blocks[(currentIndex + delta + B.blocks.length) % B.blocks.length].id);
  const patch = chase.queue.find((entry) => entry.blockId === block.id);
  return (
    <div className="w-full w-review-theme">
      <header className="w-full-h">
        <span className="w-full-t"><BI.code size={16} style={{ color: 'var(--brand-orange)' }} /> Code editor</span>
        <div className="w-seg"><button className={scope === 'block' ? 'on' : ''} onClick={() => {setScope('block');setMessage(null);}}>This block</button><button className={scope === 'file' ? 'on' : ''} onClick={() => {setScope('file');setMessage(null);}}>Whole file</button></div>
        {scope === 'block' && <span className="w-step"><button onClick={() => step(-1)} title="Previous block"><BI.chevron size={12} style={{ transform: 'rotate(180deg)' }} /></button><span className="w-step-n">{currentIndex + 1}/{B.blocks.length}</span><button onClick={() => step(1)} title="Next block"><BI.chevron size={12} /></button><span className="w-step-l">{block.label}</span></span>}
        {scope === 'file' && <div className="w-seg"><button className={split ? '' : 'on'} onClick={() => setSplit(false)} title="Editor only">Editor</button><button className={split ? 'on' : ''} onClick={() => setSplit(true)} title="Editor beside the live render — the render follows your unapplied text">Split</button></div>}
        {scope === 'file' && split && <span className="w-step-l" style={{ marginLeft: 2 }}>{block.label}</span>}
        {scope === 'block' && patch && <span className="w-pill-ok">patch staged — accept or discard before hand-editing</span>}
        <span className="w-grow" style={{ flex: 1 }}></span>
        <span className="w-full-msg" role={boundaryWarning ? 'status' : undefined}>{editor.stale ? 'Base changed — copy your Code edits, then revert and reapply.' : boundaryWarning || message || 'One retained Code draft · Apply includes all Code edits · Close keeps them'}</span>
        {editor.canUndo && <button className="btn btn-quiet btn-sm" onClick={editor.undo}>Undo Code</button>}
        {dirty && <button className="btn btn-quiet btn-sm" onClick={() => {editor.discard();setMessage(null);}}>Revert Code</button>}
        <button className="btn btn-primary btn-sm" disabled={!dirty || editor.stale} onClick={apply} title="Apply all retained Code edits to the local draft; server QA is still required"><BI.check size={13} /> Apply Code draft</button>
        <button className="btn btn-ghost btn-sm" onClick={onClose}><BI.shrink size={14} /> Close</button>
      </header>
      <div className={'w-full-b' + (scope === 'file' && split ? ' split' : '')}>
        {scope === 'block' ? draftBlock?.range ? <CmBox key={'full-' + block.id} value={blockValue} onChange={value => editor.setBlock(block.id, value)} height="100%" /> : <div role="alert">Section boundaries changed. Use Whole file to repair the retained draft.</div> : <React.Fragment>
          <CmBox key="full-whole" value={wholeValue} onChange={(value) => {editor.setWhole(value);setMessage(null);}} height="100%" marks={split ? lineMarks : null} sel={split ? block.id : null} onSelBlock={split ? B.setSel : null} />
          {split && <div className="w-full-pv"><div className="w-full-pv-h"><i className="w-dot" style={{ background: fileDirty ? 'var(--cond)' : 'var(--pass)' }}></i>{fileDirty ? 'Preview of your unapplied text' : 'Live build'}<span className="w-grow" style={{ flex: 1 }}></span>{boundaryWarning ? <span className="w-full-pv-e" data-code-boundary-warning title={unlocated.map(entry => entry.label || entry.id).join(', ')}>{boundaryWarning}</span> : <span className="w-meta">{lineMarks.length} blocks · 600px</span>}</div><div className="w-full-pv-b"><BeFrame html={wholeValue} sel={block.previewId || block.id} onSel={(id) => B.setSel(B.fromPreview ? B.fromPreview(id) : id)} width={600} fit={true} resizeKey={'fullsplit:' + (fileDirty ? 'd' : 'c')} artifactUrl={isLive ? artifactUrl : null} isolated={isolated} /></div></div>}
        </React.Fragment>}
      </div>
    </div>
  );
}

function RwStatStrip({ items }) {
  const element = React.useRef(null);
  const fit = () => {
    const node = element.current;if (!node) return;
    const row = node.parentElement;
    let available = node.clientWidth;
    if (row) {
      const style = window.getComputedStyle(row);
      const gap = parseFloat(style.columnGap) || 14;
      const children = [...row.children];
      const spacer = children.findIndex((child) => child.classList.contains('w-grow'));
      const trailing = spacer > -1 ? children.slice(spacer + 1).filter((child) => window.getComputedStyle(child).display !== 'none') : [];
      const left = node.getBoundingClientRect().left;
      const rowRect = row.getBoundingClientRect();
      const innerRight = rowRect.right - (parseFloat(style.paddingRight) || 0);
      let tail = 0;
      trailing.forEach((child) => {tail += child.getBoundingClientRect().width + gap;});
      const stop = innerRight - tail;
      if (stop > left) available = stop - left - gap;
    }
    if (available <= 0) return false;
    const more = node.querySelector('.w-stat.more');
    const cells = [...node.querySelectorAll('.w-stat')].filter((cell) => cell !== more);
    cells.forEach((cell) => {cell.style.display = '';cell.classList.remove('squeeze');});
    if (more) more.style.display = 'none';
    const widths = cells.map((cell) => cell.getBoundingClientRect().width);
    let total = widths.reduce((sum, width) => sum + width, 0);
    let hidden = 0;
    for (let index = cells.length - 1; index > 0 && total > available; index--) {
      cells[index].style.display = 'none';
      total -= widths[index];
      if (!hidden) total += 34;
      hidden++;
    }
    if (hidden && more) {
      more.style.display = '';
      more.textContent = '+' + hidden;
      more.title = items.slice(cells.length - hidden).map((item) => item.t).join(' · ');
    }
    if (total > available && cells[0]) cells[0].classList.add('squeeze');
    return true;
  };
  React.useLayoutEffect(() => {
    const node = element.current;if (!node) return;
    if (fit() === false) {
      let tries = 0;
      const again = () => {if (fit() === false && tries++ < 30) setTimeout(again, 40);};
      setTimeout(again, 0);
    }
    const frame = requestAnimationFrame(fit);
    const watch = setInterval(fit, 300);
    if (window.ResizeObserver) {
      const twice = () => {fit();requestAnimationFrame(fit);};
      const observer = new ResizeObserver(twice);
      observer.observe(node);
      if (node.parentElement) observer.observe(node.parentElement);
      return () => {observer.disconnect();cancelAnimationFrame(frame);clearInterval(watch);};
    }
    window.addEventListener('resize', fit);
    return () => {window.removeEventListener('resize', fit);cancelAnimationFrame(frame);clearInterval(watch);};
  });
  return <span className="w-stats" ref={element} title={items.map((item) => item.t).join(' · ')}>{items.map((item) => <span key={item.k} className={'w-stat' + (item.ok ? ' ok' : '')}>{item.node}</span>)}<span className="w-stat more" style={{ display: 'none' }}></span></span>;
}

function rwNotificationAge(value) {
  const time = typeof value === 'number' ? value : Date.parse(value);
  if (!Number.isFinite(time)) return 'just now';
  const seconds = Math.max(0, Math.round((Date.now() - time) / 1000));
  if (seconds < 45) return 'just now';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return minutes + 'm ago';
  const hours = Math.round(minutes / 60);
  return hours < 24 ? hours + 'h ago' : Math.round(hours / 24) + 'd ago';
}

function rwNotificationActor(actorId) {
  const mav = window.MAV || {};
  const people = [mav.currentUser, ...(Array.isArray(mav.team) ? mav.team : [])].filter(Boolean);
  const person = people.find((entry) => entry.id === actorId || entry._id === actorId || entry.user_id === actorId);
  return person && person.name || (actorId ? 'A teammate' : 'System');
}

function rwCachedUnreadNotifications() {
  const mav = window.MAV || {};
  const candidates = [
    mav.currentUser && mav.currentUser.unreadNotifications,
    mav.me && typeof mav.me === 'object' ? mav.me.unreadNotifications : undefined,
  ];
  const value = candidates.find((candidate) => candidate !== null && candidate !== undefined && candidate !== '' && Number.isFinite(Number(candidate)));
  return value == null ? null : Math.max(0, Number(value));
}

function rwUpdateCachedUnreadNotifications(value) {
  const mav = window.MAV || {};
  [mav.currentUser, mav.me].forEach((target) => {
    if (target && typeof target === 'object' && Object.prototype.hasOwnProperty.call(target, 'unreadNotifications')) {
      target.unreadNotifications = value;
    }
  });
}

function RwNotificationBell({ isLive, onOpenThread }) {
  const api = window.GooseAPI;
  const capable = !!(isLive && api
    && typeof api.listNotifications === 'function'
    && typeof api.markNotificationRead === 'function'
    && typeof api.markAllNotificationsRead === 'function');
  const [open, setOpen] = React.useState(false);
  const [rows, setRows] = React.useState([]);
  const [unread, setUnread] = React.useState(null);
  const [state, setState] = React.useState(capable ? 'loading' : 'unavailable');
  const [error, setError] = React.useState(null);
  const [busy, setBusy] = React.useState(null);
  const [position, setPosition] = React.useState({ top: 64, right: 12 });
  const button = React.useRef(null);
  const request = React.useRef(0);

  const mapRows = (result) => window.GooseReviewComments && typeof window.GooseReviewComments.mapNotificationEnvelope === 'function'
    ? window.GooseReviewComments.mapNotificationEnvelope(result)
    : [];
  const loadUnread = async () => {
    if (!capable) return;
    const token = ++request.current;
    setState('loading');
    setError(null);
    try {
      const cached = rwCachedUnreadNotifications();
      const result = await api.listNotifications({ unread: 1 });
      if (request.current !== token) return;
      const next = mapRows(result);
      setRows(next);
      setUnread(cached == null ? next.length : cached);
      setState('ready');
    } catch (nextError) {
      if (request.current !== token) return;
      setRows([]);
      setUnread(null);
      setState('error');
      setError(nextError && nextError.data && nextError.data.error || nextError && nextError.message || 'Notifications could not be loaded.');
    }
  };

  React.useEffect(() => {
    if (!capable) {
      request.current++;
      setRows([]);
      setUnread(null);
      setState('unavailable');
      return;
    }
    loadUnread();
    return () => {request.current++;};
  }, [capable]);

  const toggle = () => {
    const rect = button.current && button.current.getBoundingClientRect();
    if (rect) setPosition({ top: rect.bottom + 8, right: Math.max(12, window.innerWidth - rect.right) });
    setOpen((value) => !value);
  };
  const readOne = async (notification) => {
    if (!capable || busy) return;
    setBusy(notification.id);
    setError(null);
    try {
      await api.markNotificationRead(notification.id);
      const nextUnread = unread == null ? null : Math.max(0, unread - 1);
      setRows((items) => items.filter((item) => item.id !== notification.id));
      setUnread(nextUnread);
      if (nextUnread != null) rwUpdateCachedUnreadNotifications(nextUnread);
      if (onOpenThread) await onOpenThread(notification);
      setOpen(false);
    } catch (nextError) {
      setError(nextError && nextError.data && nextError.data.error || nextError && nextError.message || 'The notification could not be opened.');
    } finally {
      setBusy(null);
    }
  };
  const readAll = async () => {
    if (!capable || busy) return;
    setBusy('all');
    setError(null);
    try {
      await api.markAllNotificationsRead();
      setRows([]);
      setUnread(0);
      rwUpdateCachedUnreadNotifications(0);
    } catch (nextError) {
      setError(nextError && nextError.data && nextError.data.error || nextError && nextError.message || 'Notifications could not be marked read.');
    } finally {
      setBusy(null);
    }
  };

  const menu = open && ReactDOM.createPortal(
    <div className="w-review-theme" data-notifications-overlay="1" style={{ position: 'fixed', inset: 0, zIndex: 1000 }} onMouseDown={() => setOpen(false)}>
      <section role="dialog" aria-label="Notifications" onMouseDown={(event) => event.stopPropagation()} style={{
        position: 'fixed', top: position.top, right: position.right, width: 360, maxWidth: 'calc(100vw - 24px)',
        maxHeight: 'min(520px, calc(100vh - ' + position.top + 'px - 12px))', display: 'flex', flexDirection: 'column',
        flexShrink: 0, overflow: 'hidden', borderRadius: 16, background: 'var(--surface)', color: 'var(--ink)',
        border: '1px solid color-mix(in srgb,var(--hairline) 75%,transparent)', boxShadow: '0 24px 64px -20px rgba(0,0,0,.5)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 16px', flexShrink: 0, borderBottom: '1px solid color-mix(in srgb,var(--hairline) 55%,transparent)' }}>
          <b style={{ fontSize: 14 }}>Notifications</b>
          {unread > 0 && <span className="w-tag" style={{ color: 'var(--rw-cond-ink)', background: 'var(--cond-bg)' }}>{unread} unread</span>}
          <span className="w-grow" style={{ flex: 1 }}></span>
          {unread > 0 && <button type="button" className="w-cm-act" data-notifications-mark-all="1" disabled={!!busy} onClick={readAll}>{busy === 'all' ? 'Marking…' : 'Mark all read'}</button>}
        </div>
        <div style={{ minHeight: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
          {state === 'loading' && <div className="w-bar-note" style={{ padding: 16, flexShrink: 0 }}>Loading notifications…</div>}
          {state === 'unavailable' && <div className="w-bar-note" style={{ padding: 16, flexShrink: 0 }}>Notifications are unavailable in this lane.</div>}
          {state === 'error' && <div role="alert" className="w-bar-note" style={{ padding: 16, color: 'var(--fail)', flexShrink: 0 }}>{error}</div>}
          {state === 'ready' && !rows.length && <div className="w-bar-note" style={{ padding: 16, flexShrink: 0 }}>No unread notifications.</div>}
          {rows.map((notification) => {
            const KindIcon = notification.kind === 'mention' ? BI.at : notification.kind === 'resolve' ? BI.check : BI.chat;
            return <button type="button" key={notification.id} data-notification-id={notification.id} data-notification-kind={notification.kind}
              disabled={!!busy} onClick={() => readOne(notification)} style={{
                display: 'flex', alignItems: 'flex-start', gap: 11, padding: '12px 16px', textAlign: 'left', flexShrink: 0,
                color: 'var(--ink)', background: 'transparent', borderBottom: '1px solid color-mix(in srgb,var(--hairline) 45%,transparent)',
              }}>
              <span data-notification-icon={notification.kind} style={{ display: 'grid', placeItems: 'center', width: 28, height: 28, flex: '0 0 28px', borderRadius: 9, color: 'var(--brand-orange)', background: 'var(--sink)' }}><KindIcon size={14} /></span>
              <span style={{ minWidth: 0, flex: 1 }}>
                <span style={{ display: 'flex', alignItems: 'baseline', gap: 7, flexWrap: 'wrap' }}><b data-notification-actor="1" style={{ fontSize: 12.5 }}>{rwNotificationActor(notification.actorId)}</b><i data-notification-age="1" style={{ fontStyle: 'normal', fontSize: 10.5, color: 'var(--ink-2)' }}>{notification.kind} · {rwNotificationAge(notification.createdAt)}</i></span>
                <span data-notification-excerpt="1" style={{ display: 'block', marginTop: 3, fontSize: 12, lineHeight: 1.45, color: 'var(--ink-2)', overflowWrap: 'anywhere' }}>{notification.bodyExcerpt || 'Open comment thread'}</span>
              </span>
            </button>;
          })}
          {error && state !== 'error' && <div role="alert" className="w-bar-note" style={{ padding: '10px 16px', color: 'var(--fail)', flexShrink: 0 }}>{error}</div>}
        </div>
      </section>
    </div>,
    document.body,
  );

  return (
    <React.Fragment>
      <span style={{ position: 'relative', display: 'inline-flex', flexShrink: 0 }}>
        <button ref={button} type="button" className="btn btn-ghost btn-sm" data-notifications-bell="1" aria-label="Notifications" aria-expanded={open ? 'true' : 'false'} onClick={toggle} title={capable ? 'Notifications' : 'Notifications unavailable'}><I.bell size={14} /></button>
        {unread > 0 && <span data-notifications-unread={unread} style={{ position: 'absolute', top: -5, right: -5, minWidth: 17, height: 17, padding: '0 4px', display: 'grid', placeItems: 'center', borderRadius: 20, background: '#8A6206', color: '#fff', fontSize: 9.5, fontWeight: 800, pointerEvents: 'none' }}>{unread}</span>}
      </span>
      {menu}
    </React.Fragment>
  );
}

let RW_PENDING_COMMENT_LINK = null;

function RwResetEditsButton({ disabled, html, codeHtml, revisionKey, onReset }) {
  const [deadline, setDeadline] = React.useState(0);
  React.useEffect(() => { setDeadline(0); }, [disabled, html, codeHtml, revisionKey]);
  React.useEffect(() => {
    if (!deadline) return;
    const timer = setTimeout(() => setDeadline(0), Math.max(0, deadline - Date.now()));
    return () => clearTimeout(timer);
  }, [deadline]);
  return <button type="button" className="btn btn-danger btn-lg" data-action="reset-edits"
    data-reset-armed={deadline ? '1' : '0'} disabled={disabled}
    title="Press twice within 5 seconds to discard all unsaved Manual and Code edits. Saved revisions and assets stay unchanged."
    onBlur={() => setDeadline(0)}
    onKeyDown={event => { if (event.repeat) event.preventDefault();if (event.key === 'Escape') setDeadline(0); }}
    onClick={() => {
      if (disabled) return;
      if (deadline > Date.now()) { setDeadline(0);onReset(); }
      else setDeadline(Date.now() + 5000);
    }}><BI.x size={15} /><span aria-live="polite">{deadline ? 'Press again to reset' : 'Reset edits'}</span></button>;
}

function ReviewWorkspace(props) {
  const [head, setHead] = React.useState(null);
  const context = JSON.stringify([window.MAV.__contextKey, window.MAV.__contextEpoch]);
  React.useEffect(() => { setHead(null); }, [context]);
  const seed = head && head.jobId === props.job.id && head.context === context ? head : null;
  // useBuildBlocks owns local drafts but has no reset seam. Remount only after
  // amendment refresh has loaded the exact head HTML, giving it a new baseline.
  return <RwAmendWorkspace key={context + ':' + props.job.id + ':' + (seed ? seed.generation : 0)} {...props} initialHead={seed}
    onDraftReset={(next) => setHead((old) => ({ ...next, context, jobId: props.job.id, generation: (old ? old.generation : 0) + 1 }))} />;
}

function RwAmendWorkspace({ job, go, back, openJob, initialHead, onDraftReset }) {
  const initialUi = initialHead && initialHead.ui || {};
  const client = window.MAV.clientBySlug(job.client);
  const isLive = !!job._id;
  const mockHtml = () => window.buildEmailHtml(client.name, client.accent || client.color, '#fff');
  const [html, setHtml] = React.useState(() => initialHead ? initialHead.html : isLive ? '' : mockHtml());
  const [htmlState, setHtmlState] = React.useState(() => initialHead ? 'ready' : isLive ? 'loading' : 'ready');
  const [sessionLoad, setSessionLoad] = React.useState(() => ({
    jobId: job.id,
    state: initialHead ? 'ready' : !isLive ? 'mock' : window.GooseAPI && typeof window.GooseAPI.editorSession === 'function' ? 'loading' : 'legacy',
    data: initialHead ? initialHead.session : null,
  }));
  const [graphHead, setGraphHead] = React.useState(null);
  const [sessionRefresh, setSessionRefresh] = React.useState(0);
  const [sessionRefreshing, setSessionRefreshing] = React.useState(false);
  const [amend, setAmend] = React.useState(() => initialHead ? initialHead.amend : { state: 'idle' });
  const amendBusy = React.useRef(false);
  const bundleGeneration = React.useRef(0);
  const workspaceAlive = React.useRef(true);
  const workspaceIdentity = JSON.stringify([window.MAV.__contextKey, window.MAV.__contextEpoch, job.id]);
  const liveWorkspaceIdentity = React.useRef(workspaceIdentity);
  liveWorkspaceIdentity.current = workspaceIdentity;
  React.useEffect(() => {
    workspaceAlive.current = true;
    return () => { workspaceAlive.current = false;bundleGeneration.current++; };
  }, [workspaceIdentity]);
  const resetDrafts = React.useRef(null);
  const seeded = React.useRef(!!initialHead);
  const retainedEditor = React.useRef(null);
  const [qaMarkdown, setQaMarkdown] = React.useState('');
  const [qaSummary, setQaSummary] = React.useState(null);
  const [revisionQaLoad, setRevisionQaLoad] = React.useState(null);
  const [imgRefAudit, setImgRefAudit] = React.useState(null);
  const [textAccuracy, setTextAccuracy] = React.useState(null);
  const [afterburnerStatus, setAfterburnerStatus] = React.useState(() => isLive ? undefined : { status: 'not_started' });
  const [receipt, setReceipt] = React.useState(() => (job && job.engine === 'sabre' ? undefined : null));   // RW-02a integration (🐓): undefined = run receipt in flight; null = absent/legacy
  const builtRenderTries = React.useRef({});
  const [builtReady, setBuiltReady] = React.useState(0);

  React.useEffect(() => {builtRenderTries.current = {};setBuiltReady(0);}, [job.id]);
  React.useEffect(() => {setGraphHead(null);setSessionRefreshing(false);}, [job.id]);
  React.useEffect(() => {
    const refresh = (event) => {
      if (event && event.detail && event.detail.jobId && event.detail.jobId !== job.id) return;
      setGraphHead(null);
      setSessionRefreshing(true);
      setSessionRefresh((value) => value + 1);
    };
    window.addEventListener('rw-session-refresh', refresh);
    return () => window.removeEventListener('rw-session-refresh', refresh);
  }, [job.id]);

  React.useEffect(() => {
    let alive = true;
    let adopted = false;
    if (seeded.current) {seeded.current = false;return () => {alive = false;};}
    if (!isLive) {
      setSessionRefreshing(false);
      setSessionLoad({ jobId: job.id, state: 'mock', data: null });
      setHtml(mockHtml());
      setHtmlState('ready');
      return () => {alive = false;};
    }
    // A same-job asset refresh retains the exact session/HTML until its successor
    // arrives, while sessionRefreshing immediately blocks approval against the old CAS.
    const refreshing = sessionRefreshing && sessionLoad.jobId === job.id && sessionLoad.state === 'ready';
    if (!refreshing) {
      setHtml('');
      setHtmlState('loading');
    }

    const loadLegacyHtml = async () => {
      if (!(window.GooseAPI && window.GooseAPI.fetchArtifactText)) {
        if (alive) setHtmlState('missing');
        return;
      }
      try {
        const result = await window.GooseAPI.fetchArtifactText(job.id, '03_execution/index.html');
        if (!alive) return;
        if (result && result.text && result.text.indexOf('<') !== -1) {
          setHtml(result.text);
          setHtmlState('ready');
        } else setHtmlState('missing');
      } catch (_) {if (alive) setHtmlState('missing');}
    };

    (async () => {
      const api = window.GooseAPI;
      if (!(api && typeof api.editorSession === 'function')) {
        if (!alive) return;
        setSessionLoad({ jobId: job.id, state: 'legacy', data: null });
        await loadLegacyHtml();
        return;
      }
      if (!refreshing) setSessionLoad({ jobId: job.id, state: 'loading', data: null });
      let session;
      try {
        session = await api.editorSession(job.id);
      } catch (error) {
        if (!alive) return;
        const drift = error && error.status === 409 && rwApiErrorCode(error) === 'projection_drift';
        const legacy = error && (error.status === 401 || error.status === 404);
        setSessionLoad({
          jobId: job.id,
          state: drift ? 'drift' : legacy ? 'legacy' : 'unavailable',
          data: null,
          error: !drift && !legacy ? error : null,
          // TE-123 A: graphError spreads detail into the flat response body.
          drift: drift ? Object.fromEntries(Object.entries(error.data || {}).filter(([key]) => !['code', 'error'].includes(key))) : null,
        });
        await loadLegacyHtml();
        return;
      }
      if (!alive) return;
      const resolvedSession = rwResolveSessionUrls(session);
      try {
        const text = await rwFetchReturnedText(resolvedSession && resolvedSession.artifacts && resolvedSession.artifacts.html_url);
        if (!alive) return;
        if (text && text.indexOf('<') !== -1) {
          if (resetDrafts.current) {
            const expected = resetDrafts.current;
            if (expected.awaitExplicitReload) return;
            if (expected.revision && (expected.revision !== resolvedSession.revision
              || expected.revision_digest && expected.revision_digest !== resolvedSession.revision_digest)) {
              setAmend({ state: 'error', error: { code: 'bundle_head_moved', action: 'reload',
                title: 'The current head is not the revision just saved.',
                detail: 'Your change is retained in history. Reload to inspect the current head before making another change.' } });
              adopted = true; // Preserve this specific refusal instead of the generic read failure.
              return;
            }
            adopted = true;
            onDraftReset({ html: text, session: resolvedSession, amend: resetDrafts.current,
              ui: { tab, view, vp, zoom, preview, pre, notesOpen, notes } });
            return;
          }
          const retained = retainedEditor.current;
          const sameHead = retained?.session?.revision === resolvedSession.revision
            && retained?.session?.revision_digest === resolvedSession.revision_digest;
          if (retained?.dirty && retained.session && !sameHead) {
            // A peer/asset refresh may move the server head. Keep the developer's
            // bytes accessible, but never silently rebase or apply them there.
            setGraphHead({ revision: resolvedSession.revision, revision_digest: resolvedSession.revision_digest });
            return;
          }
          if (!sameHead || !retained?.dirty) setHtml(text);
          setHtmlState('ready');
          setSessionLoad({ jobId: job.id, state: 'ready', data: resolvedSession });
        } else {
          setHtmlState('missing');
          setSessionLoad({ jobId: job.id, state: 'unavailable', data: resolvedSession, error: new Error('Revision HTML is unavailable.') });
          await loadLegacyHtml();
        }
      } catch (error) {
        if (alive) {
          setHtmlState('missing');
          setSessionLoad({ jobId: job.id, state: 'unavailable', data: resolvedSession, error });
          await loadLegacyHtml();
        }
      }
    })().finally(() => {
      if (!alive) return;
      setSessionRefreshing(false);
      if (!adopted && amendBusy.current && resetDrafts.current && !resetDrafts.current.awaitExplicitReload) {
        // Publication succeeded but adopting the exact retained successor failed.
        // Keep all mutation paths locked; retrying the read must not repeat POST.
        setAmend({ state: 'error', error: { code: 'bundle_refresh_required', action: 'reload',
          title: 'Change saved; reload the retained revision before continuing.',
          detail: 'The server saved the change, but its editor session could not be loaded. Reload; do not resubmit the edit.' } });
      }
    });
    return () => {alive = false;};
  }, [isLive, job.id, sessionRefresh]);

  React.useEffect(() => {
    setQaMarkdown('');
    setQaSummary(null);
    setImgRefAudit(null);
    setTextAccuracy(null);
    setReceipt(isLive && job.engine === 'sabre' ? undefined : null);
    if (!isLive || !(window.GooseAPI && window.GooseAPI.fetchArtifactText)) return;
    if (sessionLoad.jobId !== job.id || sessionLoad.state === 'loading') return;
    let alive = true;
    (async () => {
      try {
        const result = await window.GooseAPI.fetchArtifactText(job.id, '04_qa/qa-report.md');
        if (alive && result && result.text) {setQaMarkdown(result.text);setQaSummary(parseQaSummary(result.text));}
      } catch (_) {}
      try {
        const result = await window.GooseAPI.fetchArtifactText(job.id, '04_qa/image-ref-audit.json');
        if (alive && result && result.text) {try {setImgRefAudit(JSON.parse(result.text));} catch (_) {}}
      } catch (_) {}
      try {
        const result = await window.GooseAPI.fetchArtifactText(job.id, '04_qa/text-accuracy-audit.json');
        if (alive && result && result.text) {try {setTextAccuracy(JSON.parse(result.text));} catch (_) {}}
      } catch (_) {}
      if (job.engine === 'sabre') {
        try {
          const result = await window.GooseAPI.fetchArtifactText(job.id, '05_approval/sabre-run-receipt.v1.json');
          if (alive && result && result.text) {try {setReceipt(JSON.parse(result.text));} catch (_) {setReceipt(null);}}
          else if (alive) setReceipt(null);
        } catch (_) {if (alive) setReceipt(null);}
      }
    })();
    return () => {alive = false;};
  }, [isLive, job.id, job.engine, sessionLoad.jobId, sessionLoad.state]);

  React.useEffect(() => {
    if (!isLive) {setAfterburnerStatus({ status: 'not_started' });return;}
    setAfterburnerStatus(undefined);
    if (sessionLoad.jobId !== job.id || sessionLoad.state === 'loading') return;
    if (!(window.GooseAPI && window.GooseAPI.afterburnerStatus)) {setAfterburnerStatus(null);return;}
    let alive = true;
    (async () => {
      try {
        const next = await window.GooseAPI.afterburnerStatus(job.id);
        if (alive) setAfterburnerStatus(next || null);
      } catch (_) {if (alive) setAfterburnerStatus(null);}
    })();
    return () => {alive = false;};
  }, [isLive, job.id, sessionLoad.jobId, sessionLoad.state, workspaceIdentity]);

  const activeSession = sessionLoad.jobId === job.id && sessionLoad.state === 'ready' ? sessionLoad.data : null;
  const qaKey = revisionQaKey(job.id, activeSession, String(window.MAV.__contextKey || '') + ':' + String(window.MAV.__contextEpoch || ''));
  React.useEffect(() => {
    let alive = true;
    if (!isLive || !qaKey || !activeSession?.artifacts?.qa_receipt) return;
    setRevisionQaLoad({ key: qaKey, state: 'loading' });
    fetchRevisionQa(window.GooseAPI, job.id, activeSession).then(receipt => {
      if (alive) setRevisionQaLoad({ key: qaKey, state: 'ready', receipt });
    }).catch(error => {
      if (alive) setRevisionQaLoad({ key: qaKey, state: 'error', error: error.message });
    });
    return () => { alive = false; };
  }, [isLive, qaKey, activeSession?.artifacts?.qa_receipt, sessionRefresh]);
  const reviewedRevision = activeSession;
  const sessionFindings = activeSession && Array.isArray(activeSession.findings) ? activeSession.findings : null;
  const projection = activeSession && activeSession.projection;
  const unboundAnchors = projection && Array.isArray(projection.unbound_anchors) ? projection.unbound_anchors.length : 0;
  const unmappedRegions = projection && Array.isArray(projection.unmapped_regions) ? projection.unmapped_regions.length : 0;
  const shellFinding = sessionFindings && sessionFindings.find((finding) => finding && finding.code === 'shell_unrecognized' && finding.scope === 'session') || null;
  const findings = React.useMemo(
    () => sessionFindings !== null && projection
      ? []
      : isLive ? window.GooseReviewBlocks.parseQaFindings(qaMarkdown) : window.MAV.reviewFindings,
    [isLive, qaMarkdown, sessionFindings, projection],
  );
  const anchoredProjection = !!(projection && (projection.source === 'builder' || ['authored', 'mixed'].includes(projection.identity)));
  const localIdentity = React.useMemo(() => anchoredProjection ? null : window.GooseReviewBlocks.findBands(html).identity, [anchoredProjection, html]);
  const projectionMismatch = !!(projection && !anchoredProjection && projection.source === 'overlay' && projection.identity !== localIdentity);
  const useProjection = anchoredProjection || projectionMismatch;
  const projectionModel = React.useMemo(
    () => projection ? rwProjectionModel(projection, html, sessionFindings) : null,
    [projection, html, sessionFindings],
  );
  const baseB = useBuildBlocks({ html, setHtml, findings, isLive, model: useProjection ? projectionModel : null, projection,
    revisionIdentity: activeSession ? [job.id, activeSession.revision, activeSession.revision_digest] : null,
    canMutate: () => !amendBusy.current && !sessionRefreshing && !(activeSession && graphHead && graphHead.revision
      && (graphHead.revision !== activeSession.revision || graphHead.revision_digest && graphHead.revision_digest !== activeSession.revision_digest)) });
  // Display-only ids can still come from the preview overlay. They never supply
  // an edit range or wire target; exact source ranges join them to the projection.
  const previewBands = React.useMemo(() => window.GooseReviewBlocks.findBands(html).bands, [html]);
  const projectionToPreview = new Map();
  const previewToProjection = new Map();
  if (projectionModel) (useProjection ? baseB.blocks : projectionModel.blocks).forEach((block) => {
    const exact = previewBands.find((candidate) => candidate.sec === block.id)
      || previewBands.find((candidate) => candidate.range && block.range
        && candidate.range.start === block.range.start && candidate.range.end === block.range.end);
    if (!exact) return;
    projectionToPreview.set(block.id, exact.sec);
    previewToProjection.set(exact.sec, block.id);
  });
  const projectedSelection = projectionModel && (projectionModel.blocks.some((block) => block.id === baseB.sel)
    ? baseB.sel
    : previewToProjection.get(baseB.sel) || projectionModel.blocks[0] && projectionModel.blocks[0].id);
  const projectedBlocks = projectionModel && baseB.blocks.map((block) => ({
    ...block,
    previewId: projectionToPreview.get(block.id) || block.id,
  }));
  const overlayBlocks = projectionModel && !useProjection ? baseB.blocks.map((block) => {
    const projectedId = previewToProjection.get(block.id) || block.id;
    const projected = projectionModel.blocks.find((candidate) => candidate.id === projectedId);
    return projected ? { ...block, qa: projected.qa, note: projected.note, review_state: projected.review_state } : block;
  }) : null;
  const overlayCurrent = overlayBlocks && (overlayBlocks.find((block) => block.id === baseB.sel) || overlayBlocks[0]);
  const B = useProjection ? {
    ...baseB,
    ok: projectedBlocks.length > 0,
    reason: projectedBlocks.length ? null : 'no-bands',
    identity: projection.identity || projectedBlocks[0] && projectedBlocks[0].identity || 'none',
    blocks: projectedBlocks,
    unplaced: projectionModel.unplaced,
    sel: projectedSelection,
    setSel: (id) => baseB.setSel(id),
    previewSel: projectionToPreview.get(projectedSelection) || projectedSelection,
    fromPreview: (id) => previewToProjection.get(id) || id,
    cur: projectedBlocks.find((block) => block.id === projectedSelection) || projectedBlocks[0],
    hasFields: (id) => {
      const block = projectedBlocks.find((entry) => entry.id === id);
      return !!(block && Object.values(block.fields || {}).some(field => field?.schema === 'ArtifactField.v1'));
    },
  } : overlayBlocks ? { ...baseB, blocks: overlayBlocks, unplaced: projectionModel.unplaced, cur: overlayCurrent } : baseB;
  const projectionReadOnly = sessionLoad.jobId === job.id && sessionLoad.state === 'drift';
  const sessionUnavailable = sessionLoad.jobId === job.id && sessionLoad.state === 'unavailable';
  const sessionHeadMoved = !!(activeSession && graphHead && graphHead.revision
    && (graphHead.revision !== activeSession.revision || graphHead.revision_digest && graphHead.revision_digest !== activeSession.revision_digest));
  const revisionHeadMoved = sessionHeadMoved;
  const workspaceReadOnly = projectionReadOnly || sessionUnavailable || revisionHeadMoved;
  const draftDirty = B.editor.dirty || B.hist.length > 0 && html !== B.hist[0].snap;
  retainedEditor.current = { session: activeSession, dirty: draftDirty };
  // Shared synchronous gate survives block/tab remounts. A committed bundle stays
  // locked through session adoption; only the new workspace instance unlocks it.
  B.bundleMutation = async (kind, operation) => {
    if (amendBusy.current || sessionRefreshing || workspaceReadOnly || draftDirty || !activeSession) return null;
    amendBusy.current = true;
    const generation = ++bundleGeneration.current, identity = workspaceIdentity;
    const current = () => workspaceAlive.current && liveWorkspaceIdentity.current === identity && bundleGeneration.current === generation;
    setAmend({ state: 'running', operation: kind });setGate(false);
    try {
      const result = await operation();
      if (!current()) return null;
      const revision = typeof result?.revision === 'object' ? result.revision : result;
      if (!revision?.revision || !revision?.revision_digest) throw Error('The saved bundle response is incomplete. Reload before retrying.');
      const next = { state: 'bundle_saved', revision: revision.revision, revision_digest: revision.revision_digest, operation: kind };
      resetDrafts.current = next;setAmend(next);
      window.dispatchEvent(new CustomEvent('rw-session-refresh', { detail: { jobId: job.id, reason: kind } }));
      return result;
    } catch (error) {
      if (!current()) return null;
      if (error.status >= 400 && error.status < 500 && error.status !== 408) {
        amendBusy.current = false;
        setAmend({ state: 'idle' });
      } else {
        // A lost/partial response can follow a successful commit. Never invite a
        // second POST: retain the common lock until an explicit read/reload.
        resetDrafts.current = { state: 'idle', awaitExplicitReload: true };
        setAmend({ state: 'error', error: { code: 'bundle_result_unknown', action: 'reload',
          title: 'The change result is uncertain — inspect the retained head.',
          detail: 'Reload and check history before retrying. This request will not be submitted again automatically.' } });
      }
      throw error;
    }
  };
  B.bundleBusy = amendBusy.current || sessionRefreshing;
  React.useEffect(() => {
    if (!draftDirty && !amendBusy.current) return;
    const context = window.MAV.__contextKey;
    const warn = event => { event.preventDefault();event.returnValue = ''; };
    const navigate = event => {
      if (event.detail?.context === context && !window.confirm(amendBusy.current ? 'A revision change is still in progress. Leave and check its retained result when you return?' : 'Leave this build and discard the local draft? Apply and verify it first if you want to keep it.')) event.preventDefault();
    };
    window.addEventListener('beforeunload', warn);
    window.addEventListener('goose-before-navigation', navigate);
    return () => { window.removeEventListener('beforeunload', warn);window.removeEventListener('goose-before-navigation', navigate); };
  }, [draftDirty, amend.state]);
  const rawChase = useBeChase({ blocks: B.blocks, sel: B.sel, jobId: job.id, session: activeSession,
    baseHtml: B.hist[0]?.snap || html,
    disabled: workspaceReadOnly || sessionRefreshing || amend.state === 'running',
    draftDirty,
    onAccepting: (running) => {
      if (running && amendBusy.current) return false;
      amendBusy.current = running;
      setAmend({ state: running ? 'running' : 'idle' });
      if (running) setGate(false);
      return true;
    },
    onAccepted: (result) => {
      // Hold the common mutation gate until the accepted revision is adopted.
      const next = { state: 'applied', revision: result.revision.revision, revision_digest: result.revision.revision_digest, stale: result.qa && result.qa.stale_expensive || [] };
      setAmend(next);
      resetDrafts.current = next;
    },
  });
  const isolated = window.rwIsolatedPreview ? window.rwIsolatedPreview(activeSession) : { on: false };
  /* RWB-12a (🌑 pre-read cond. 5): the signed preview base expires (RWB-12b: preview.expires_at / ttl_sec).
     Re-fetch the session to remint BEFORE expiry — the new assets_base flows into the frame's artifactUrl
     and BeFrame re-keys its document (new nonce). The URL is never patched in place. Lead = max(30 s,
     10% of the TTL), floor 1 s; a failed re-fetch drops the capability → the D9 frame (fail closed). */
  const previewExpiresAt = isolated.on && activeSession.preview ? activeSession.preview.expires_at : null;
  const previewTtl = isolated.on && activeSession.preview ? activeSession.preview.ttl_sec : null;
  React.useEffect(() => {
    if (!isolated.on || !previewExpiresAt) return;
    const expires = Date.parse(previewExpiresAt);
    if (!isFinite(expires)) return;
    const lead = Math.max(30000, (Number(previewTtl) || 0) * 100);
    const delay = Math.max(1000, expires - Date.now() - lead);
    const timer = setTimeout(() => window.dispatchEvent(new CustomEvent('rw-session-refresh', { detail: { jobId: job.id, reason: 'preview_expiry' } })), delay);
    return () => clearTimeout(timer);
  }, [isolated.on, previewExpiresAt, previewTtl, job.id]);
  const chaseUnavailable = workspaceReadOnly || !activeSession;
  const chase = chaseUnavailable ? {
    ...rawChase,
    wired: false,
    canAccept: false,
    queue: workspaceReadOnly ? [] : rawChase.queue,
    ask: async () => ({ say: 'Chase is unavailable for this revision.', patches: [] }),
    run: async () => ({ say: 'Chase is unavailable for this revision.', patches: [] }),
    accept: workspaceReadOnly ? () => {} : rawChase.accept,
    availabilityMessage: 'Reload the current editable revision to use Chase.',
    reply: rawChase.reply || 'Reload the current editable revision to use Chase.',
  } : rawChase;
  const [layout, setLayout] = rwUseLayout();
  const bodyRef = React.useRef(null);
  const bodyWidth = rwUseWidthOf(bodyRef);
  const inner = bodyWidth > 8000 ? 0 : Math.max(0, bodyWidth - 28);
  const fitted = rwFitLayout(inner, layout);
  const [tab, setTab] = React.useState(initialUi.tab || 'inspector');
  // Demo-only events never authorize live approval. Live verdicts load here,
  // independently of the selected tab, and bind the complete current QA report.
  const [sqVerdict, setSqVerdict] = React.useState(null);
  const [sqVerdictRequired, setSqVerdictRequired] = React.useState(false);
  React.useEffect(() => {setSqVerdict(null);setSqVerdictRequired(false);}, [job.id]);
  React.useEffect(() => {
    if (isLive) return;
    const onVerdict = (e) => setSqVerdict(e.detail || null);
    const onRequired = (e) => setSqVerdictRequired(!!e.detail);
    window.addEventListener('rw-verdict', onVerdict);window.addEventListener('rw-verdict-required', onRequired);
    return () => {window.removeEventListener('rw-verdict', onVerdict);window.removeEventListener('rw-verdict-required', onRequired);};
  }, [isLive]);
  const currentVerdictRequired = isLive && String(job.engine || receipt?.engine || '').toLowerCase() === 'sabre';
  const currentReview = useCurrentQualificationReview({ jobId: job.id, revisionDigest: activeSession?.revision_digest,
    enabled: currentVerdictRequired, refreshKey: sessionRefresh,
    writable: !workspaceReadOnly && !sessionRefreshing && !draftDirty && amend.state !== 'running' });
  const [view, setView] = React.useState(initialUi.view || 'render');
  const [espComparison, setEspComparison] = React.useState(null);
  const [vp, setVp] = React.useState(initialUi.vp || { mode: 'desktop', desktop: 600, mobile: 375 });
  const [zoom, setZoom] = React.useState(initialUi.zoom || 'fit');
  const [preview, setPreview] = React.useState(initialUi.preview ?? true);
  const [pre, setPre] = React.useState(() => reviewPreSendActions(isLive, initialUi.pre, window.MAV.presendActions));
  const [threads, setThreads] = React.useState(() => (isLive ? [] : window.SEED_THREADS));
  const commentFeed = window.useReviewCommentFeed({ isLive, jobId: job.id, revisionDigest: activeSession?.revision_digest, chase, setThreads });
  const [commentFocus, setCommentFocus] = React.useState(null);
  const [commentLinkNote, setCommentLinkNote] = React.useState(null);
  const [notesOpen, setNotesOpen] = React.useState(initialUi.notesOpen || false);
  const [notes, setNotes] = React.useState(initialUi.notes || '');
  const [full, setFull] = React.useState(false);
  const [gate, setGate] = React.useState(false);
  const [srcImg, setSrcImg] = React.useState(null);
  const [feedback, setFeedback] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(null);
  const [reviewError, setReviewError] = React.useState(null);
  const [reviewResult, setReviewResult] = React.useState(null);
  const previousQueue = React.useRef(new Set());
  const queuedPatchKeys = JSON.stringify(chase.queue.map(patch => [patch.pid,patch.blockId,!!patch.stale]));

  React.useEffect(() => {
    const ready = chase.queue.filter(patch => !patch.stale);
    const added = ready.find(patch => !previousQueue.current.has(JSON.stringify([patch.pid,patch.blockId])));
    previousQueue.current = new Set(ready.map(patch => JSON.stringify([patch.pid,patch.blockId])));
    if (!added || chase.acceptBlocked) return;
    chase.selectPatch(added);
    if (added.blockId !== 'document') B.setSel(added.blockId);
    setView('code');
    if (!layout.hideCtx && tab !== 'comments') setTab('chase');
  }, [queuedPatchKeys]);

  const openLinkedComment = (link) => {
    const regionExists = !!(link.regionId && B.blocks.some((block) => block.id === link.regionId));
    setThreads(link.threads);
    if (regionExists) B.setSel(link.regionId);
    setCommentLinkNote(link.regionId && !regionExists ? 'region no longer in this revision' : null);
    setCommentFocus(link.threadId);
    setTab('comments');
    setLayout({ hideCtx: false, focus: false, forceCtx: true });
  };

  const openNotificationThread = async (notification) => {
    const api = window.GooseAPI;
    if (!(api && typeof api.listComments === 'function')) throw new Error('Comments are unavailable in this lane.');
    const result = await api.listComments(notification.jobId);
    const mapper = window.GooseReviewComments;
    const mapped = mapper && typeof mapper.mapCommentEnvelope === 'function' ? mapper.mapCommentEnvelope(result) : [];
    const thread = mapped.find((item) => item.id === notification.threadId);
    if (!thread) throw new Error('That comment thread is no longer available.');
    const link = { jobId: notification.jobId, threadId: thread.id, regionId: thread.regionId, threads: mapped };
    const onThisJob = notification.jobId === job.id;
    if (onThisJob && (sessionLoad.jobId !== job.id || sessionLoad.state === 'loading' || htmlState === 'loading')) {
      RW_PENDING_COMMENT_LINK = link;
      return;
    }
    if (!onThisJob) {
      if (!openJob) throw new Error('Open the notification from its build workspace.');
      if (openJob(notification.jobId, 'review') === false) return;
      RW_PENDING_COMMENT_LINK = link;
      return;
    }
    openLinkedComment(link);
  };

  React.useEffect(() => {
    setThreads(isLive ? [] : window.SEED_THREADS);
    setCommentFocus(null);
    setCommentLinkNote(null);
  }, [job.id, isLive]);

  React.useEffect(() => {
    const link = RW_PENDING_COMMENT_LINK;
    if (!link || link.jobId !== job.id) return;
    if (sessionLoad.jobId !== job.id || sessionLoad.state === 'loading' || htmlState === 'loading') return;
    RW_PENDING_COMMENT_LINK = null;
    openLinkedComment(link);
  }, [job.id, sessionLoad.jobId, sessionLoad.state, htmlState, B.blocks.length]);

  const toggle = (id) => setPre((actions) => actions.map((action) => action.id === id ? { ...action, done: !action.done } : action));
  const done = pre.filter((action) => action.done).length;
  const allDone = done === pre.length;
  const shippable = receipt === undefined ? false : (!receipt || receipt.status === 'candidate_ready');   // RW-02a integration (🐓): never shippable while the receipt is still loading
  const needVerdict = currentVerdictRequired ? !currentVerdictReady(currentReview, activeSession?.revision_digest)
    : !isLive && sqVerdictRequired && sqVerdict?.kind !== 'approve';
  const replayPending = !!(activeSession && activeSession.replay_status === 'pending');
  const dirty = draftDirty || replayPending;
  const blockWhy = !shippable
    ? (receipt === undefined ? 'Waiting for the SABRE run receipt…' : 'Run receipt is ' + receipt.status + ' — reviewable, but the engine did not produce a shippable candidate')
    : needVerdict
      ? VERDICT_REQUIRED_COPY
      : !isLive && !allDone ? 'Complete demo pre-send actions first' : null;
  const { pass, minor, fail, ungraded } = reviewBlockCounts(B.blocks);
  const openComments = threads.filter((thread) => thread.open).length;
  const [topRef, topWidth] = rwUseMeasured();
  const railMax = () => Math.max(RW_LAYOUT.RAIL_MIN, (inner || 1e4) - RW_LAYOUT.H - (fitted.showCtx ? RW_LAYOUT.H + fitted.ctx : RW_LAYOUT.STRIP) - RW_LAYOUT.MID_MIN);
  const contextMax = () => Math.max(RW_LAYOUT.CTX_MIN, (inner || 1e4) - RW_LAYOUT.H - (fitted.showRail ? RW_LAYOUT.H + fitted.rail : RW_LAYOUT.STRIP) - RW_LAYOUT.MID_MIN);
  const midWidth = inner ? inner - (fitted.showRail ? RW_LAYOUT.H + fitted.rail : layout.focus ? 0 : RW_LAYOUT.STRIP) - (fitted.showCtx ? RW_LAYOUT.H + fitted.ctx : layout.focus ? 0 : RW_LAYOUT.STRIP) : 0;
  const viewer = window.RBAC && window.RBAC.viewer && window.RBAC.viewer();
  const roleCanReview = viewer ? !!(window.RBAC.requireRole && window.RBAC.requireRole('review')) : true;
  const roleCanEdit = viewer ? !!(window.RBAC.requireRole && window.RBAC.requireRole('edit')) : true;
  const amendUnavailable = !!(activeSession && activeSession.capabilities && activeSession.capabilities.amend === false);
  const canReview = roleCanReview && !workspaceReadOnly;
  const amendmentRunning = amend.state === 'running' || amendBusy.current;
  // RW-09 pilot fix (🐓): a session refresh must NOT unmount the Inspector (it drops the asset-replace result and
  // any panel state); the apply/certify controls carry the running/refreshing gates instead (canApply/canCertify).
  const canInspect = roleCanEdit && !workspaceReadOnly;
  const canEdit = canInspect && !amendUnavailable && !amendmentRunning && !sessionRefreshing;
  B.canRevertDraft = roleCanEdit && !workspaceReadOnly && !B.bundleBusy;
  const canRestore = roleCanEdit && !workspaceReadOnly && !amendmentRunning && !sessionRefreshing && !draftDirty;
  const canApply = draftDirty && !B.editor.dirty && isLive && canEdit && !!activeSession && !amendmentRunning && !sessionRefreshing
    && !!(window.GooseAPI && typeof window.GooseAPI.proposeAmendment === 'function');
  const canResetEdits = draftDirty && B.canRevertDraft && !amendmentRunning && !submitting;
  const resetEdits = () => {
    if (!canResetEdits || amendBusy.current || sessionRefreshing) return;
    if (B.resetEdits()) { setAmend({ state: 'idle' });setGate(false); }
  };
  const revisionQa = revisionQaView(qaKey, activeSession, revisionQaLoad, { dirty: draftDirty, headMoved: revisionHeadMoved, refreshing: sessionRefreshing });
  const needsRevisionQa = revisionCertifyAllowed(activeSession) && (replayPending || revisionQaNeedsRun(revisionQa));
  const certifyUnavailable = activeSession?.replay_status === 'exact' && !activeSession?.artifacts?.qa_receipt && !revisionCertifyAllowed(activeSession);
  const showCertify = needsRevisionQa && isLive && !!activeSession && !!qaKey && roleCanEdit && !workspaceReadOnly && !amendUnavailable
    && typeof window.GooseAPI?.certifyRevision === 'function';
  const canCertify = showCertify && !draftDirty && !revisionHeadMoved && canEdit && !amendmentRunning && !sessionRefreshing && !submitting && !B.bundleBusy
    && !!(window.GooseAPI && typeof window.GooseAPI.certifyRevision === 'function');
  const refreshSession = (reason) => window.dispatchEvent(new CustomEvent('rw-session-refresh', { detail: { jobId: job.id, reason } }));
  const mapAmendFailure = (error) => window.GooseReviewAmendments.mapAmendError(error.status, error.data || { code: error.code || error.kind, error: error.message }, { qaReceipt: activeSession?.artifacts?.qa_receipt });
  const draftTarget = window.GooseReviewAmendments.deriveTarget({
    baseHtml: B.hist[0].snap, html,
    blocks: useProjection ? (projection.blocks || []).map((block) => ({ ...block, range: block.source_range || block.range,
      identity: block.identity || (projection.identity === 'authored' ? 'authored' : 'derived') }))
      : window.GooseReviewBlocks.findBands(B.hist[0].snap).bands,
  });
  const derivedApplyCopy = draftTarget.reason === 'derived_identity'
    ? 'Apply as a document-scope change — this section is a derived overlay, not an authored region' : null;
  const applyChanges = async (documentScope = false) => {
    if (!canApply || amendBusy.current) return;
    amendBusy.current = true;
    setAmend({ state: 'running' });
    setGate(false);
    const target = !documentScope && draftTarget.scope === 'block'
      ? { scope: 'block', block_id: draftTarget.block_id } : { scope: 'document' };
    // This action has no user explanation field. Targeting discriminators are
    // UI diagnostics, never the developer's amendment rationale.
    const reason = null;
    try {
      const result = await window.GooseReviewAmendments.applyFlow({ api: window.GooseAPI, jobId: job.id, session: activeSession, html, target, reason, manualEdits: B.manualEdits });
      const next = { state: result.noop ? 'noop' : 'applied', revision: result.revision.revision, revision_digest: result.revision.revision_digest, stale: result.qa && result.qa.stale_expensive || [] };
      setAmend(next);
      resetDrafts.current = next;
      refreshSession(result.noop ? 'amend_noop' : 'amend_applied');
    } catch (error) {
      setAmend({ state: 'error', error, target });
    } finally {
      if (!resetDrafts.current) amendBusy.current = false;
    }
  };
  const certifyRevision = async () => {
    const currentContext = () => workspaceAlive.current && liveWorkspaceIdentity.current === workspaceIdentity;
    if (!canCertify || amendBusy.current || !currentContext()) return;
    amendBusy.current = true;
    setAmend({ state: 'running', certifying: true });
    try {
      const result = await window.GooseAPI.certifyRevision(job.id, activeSession.revision, { expected_head_digest: activeSession.revision_digest });
      if (!currentContext()) return;
      const next = { state: 'certified', revision: result.revision.revision, revision_digest: result.revision.revision_digest, stale: result.qa && result.qa.stale_expensive || [] };
      resetDrafts.current = next;setAmend(next);
      refreshSession('revision_certified');
    } catch (error) {
      if (!currentContext()) return;
      const mapped = mapAmendFailure(error);
      setAmend({ state: 'error', error: mapped, certifying: true });
      if (mapped.code === 'already_certified') refreshSession('already_certified');
    } finally {
      if (currentContext() && !resetDrafts.current) amendBusy.current = false;
    }
  };
  React.useEffect(() => {
    if (!workspaceReadOnly) return;
    setGate(false);
    setFull(false);
  }, [workspaceReadOnly]);
  const sessionAssetsBase = activeSession && activeSession.artifacts && activeSession.artifacts.assets_base;
  const artifactUrlCallback = React.useCallback((relative) => {
    if (activeSession) return sessionAssetsBase ? rwSessionAssetUrl(sessionAssetsBase, relative) : null;
    return window.GooseAPI.artifactUrl(job.id, relative);
  }, [job.id, activeSession, sessionAssetsBase]);
  const artifactUrl = isLive && (activeSession
    ? sessionAssetsBase
    : window.GooseAPI && window.GooseAPI.artifactUrl) ? artifactUrlCallback : null;
  const isolatedArtifactUrl = React.useCallback((relative) => new URL(String(relative).replace(/^03_execution\//, ''), isolated.assetsBase).href, [isolated.assetsBase]);
  const frameArtifactUrl = isolated.on ? isolatedArtifactUrl : artifactUrl;
  const onRunAfterburner = () => openJob ? openJob(job.id) : go && go('job');
  const onViewDiff = (id) => {if (id && id !== 'document') B.setSel(id);setView('code');};
  const onViewEspDiff = (pair, expected) => {
    if (!workspaceAlive.current || liveWorkspaceIdentity.current !== workspaceIdentity || !isLive || !roleCanEdit) return;
    if (!espOperationValid(pair, { jobId: job.id, expected, pair: true })) return;
    // Separate immutable-view state: never insert this into the editor or Chase
    // proposal queue. The canvas verifies the actual source SHA-256 values.
    setEspComparison({ context: workspaceIdentity, pair: structuredClone(pair), expected: structuredClone(expected) });
    setView('esp-source');
  };
  const activeEspComparison = roleCanEdit && espComparison?.context === workspaceIdentity ? espComparison : null;
  const onCloseEspDiff = () => { setEspComparison(null); setView('render'); };
  const onFull = canEdit && B.cur ? () => setFull(true) : null;
  const unplaced = B.unplaced;
  const rwTabProps = { job, c: client, isLive, B, chase, afterburnerStatus, receipt, sqVerdict, pre, setPre, notesOpen, notes, setNotes,
    editorSession: activeSession, draftDirty, revisionQa, currentReview, revisionHeadMoved, sessionRefreshing,
    onRunQa: showCertify ? certifyRevision : null, canRunQa: canCertify, qaRunning: amendmentRunning && !!amend.certifying,
    qaRunError: amend.state === 'error' && amend.certifying ? amend.error : null,
    afterburnerContextKey: workspaceIdentity, afterburnerWorkspaceId: window.MAV.activeWorkspaceId,
    espCanRead: roleCanEdit, espCanConvert: canEdit && !draftDirty && !revisionHeadMoved && !B.bundleBusy,
    onRunAfterburner, onViewDiff, onViewEspDiff, onCloseEspDiff, espComparison: activeEspComparison,
    espComparisonHistorical: revisionHeadMoved || sessionRefreshing || !activeSession,
    onFull, onView: setView, unplaced, vp, setVp, zoom, srcImg, setSrcImg,
    sessionArtifacts: reviewedRevision && reviewedRevision.artifacts || null };
  const onRevisionHead = React.useCallback((head) => setGraphHead(head || null), []);
  const approvalDigest = !revisionHeadMoved && activeSession && activeSession.revision_digest || null;

  const submitDecision = async (decision, flywheel = {}) => {
    if (submitting || amendBusy.current || sessionRefreshing || workspaceReadOnly || decision === 'approve' && approvalWhy) return;
    if (!isLive || !(window.GooseAPI && window.GooseAPI.reviewBuild)) {
      setReviewError('Review API not available (this looks like a mock job).');
      return;
    }
    amendBusy.current = true;
    setSubmitting(decision); setReviewError(null); setReviewResult(null);
    try {
      const body = { decision, notes: notes || undefined };
      if (decision === 'approve') {
        if (approvalDigest) body.revision_digest = approvalDigest;
        body.contributions = Array.isArray(flywheel.contributions) ? flywheel.contributions : [];
        if (flywheel.opt_out_reason) body.opt_out_reason = flywheel.opt_out_reason;
      }
      const result = await window.GooseAPI.reviewBuild(job.id, body);
      setReviewResult({ decision, build: result.build, flywheel: result.flywheel, context: workspaceIdentity, revisionDigest: approvalDigest });
      if (result && result.build && window.MAV && Array.isArray(window.MAV.jobs)) {
        const index = window.MAV.jobs.findIndex((entry) => entry.id === job.id);
        if (index >= 0) {
          window.MAV.jobs[index] = { ...window.MAV.jobs[index], status: result.build.status, verdict: result.build.verdict };
        }
      }
      if (decision === 'approve') {setGate(false);setFeedback(true);}
    } catch (error) {
      if (decision === 'approve' && /^developer_verdict_|^qualification_/.test(rwApiErrorCode(error) || '')) {
        setGate(false);currentReview.reload();
        setReviewError({ code: rwApiErrorCode(error), title: verdictError(rwApiErrorCode(error)) });
      } else if (decision === 'approve' && error.status === 400) {
        setReviewError(window.GooseReviewContributions.mapContributionError(error.status, error.data || { code: error.code, error: error.message }));
      } else {
        if (decision === 'approve') setGate(false);
        setReviewError(rwApiErrorCode(error) === 'revision_unverified'
          ? 'this revision is not yet re-verified — rerun QA'
          : (error && error.data && error.data.error) || error.message || 'Failed to submit review.');
      }
    } finally {
      amendBusy.current = false;
      setSubmitting(null);
    }
  };

  const stats = [
    { k: 'edit', t: B.touched.size + ' blocks edited', node: <React.Fragment><b>{B.touched.size}</b> edited</React.Fragment> },
    ...(chase.queue.length ? [{ k: 'staged', ok: true, t: chase.queue.length + ' patches staged', node: <React.Fragment><b>{chase.queue.length}</b> staged</React.Fragment> }] : []),
    { k: 'qa', t: pass + ' pass, ' + fail + ' fail, ' + minor + ' minor, ' + ungraded + ' ungraded', node: <React.Fragment><b>{pass}</b> pass {fail > 0 && <React.Fragment><b>{fail}</b> fail </React.Fragment>}<b>{minor}</b> minor <b>{ungraded}</b> ungraded</React.Fragment> },
    ...(textAccuracy && textAccuracy.accuracy_percent != null ? [{ k: 'accuracy', t: 'text accuracy ' + Number(textAccuracy.accuracy_percent).toFixed(1) + '%', node: <React.Fragment><b>{Number(textAccuracy.accuracy_percent).toFixed(1)}%</b> text accuracy</React.Fragment> }] : []),
    { k: 'client', t: client.name, node: client.name },
    { k: 'engine', t: [job.engine || 'engine unknown', job.source].filter(Boolean).join(' · '), node: [job.engine || 'engine unknown', job.source].filter(Boolean).join(' · ') },
    ...(openComments ? [{ k: 'comments', t: openComments + ' open comments', node: <React.Fragment><b>{openComments}</b> open</React.Fragment> }] : []),
  ];
  const editorSessionState = sessionLoad.jobId === job.id ? sessionLoad.state : 'loading';
  const sessionPending = editorSessionState === 'loading' || sessionRefreshing;
  const approvalWhy = rwApprovalBlockReason({ submitting, sessionPending, amendmentRunning, draftDirty,
    replayPending, hasSession: !!activeSession, replayStatus: activeSession && activeSession.replay_status, blockWhy });
  const currentReviewResult = reviewResult?.context === workspaceIdentity && reviewResult.revisionDigest === approvalDigest ? reviewResult : null;
  const approvedReview = currentReviewResult?.decision === 'approve' && !dirty && !workspaceReadOnly && !sessionPending ? currentReviewResult : null;
  const receiptLabel = receipt === undefined ? 'Receipt loading' : (window.RW_RECEIPT_STATES?.[receipt?.status]?.label || 'Unknown run state');
  const verdictPresentation = revisionQaPresentation(isLive ? revisionQa : { state: 'current', receipt: { verdict: job.verdict || 'CONDITIONAL' } });
  const VerdictIcon = BI[verdictPresentation.icon];
  const editBlockedCopy = projectionReadOnly
    ? 'Projection unavailable — this build stays read-only until you reload.'
    : sessionUnavailable
      ? 'The exact editor session is unavailable — this fallback view stays read-only until you reload.'
      : revisionHeadMoved
        ? 'The revision head changed — reload before editing the new head.'
        : amendUnavailable
          ? 'This revision is read-only — amendments are unavailable.'
          : null;
  const unavailableCapabilities = activeSession && activeSession.capabilities
    ? [['amend', 'amendments'], ['assets', 'asset changes'], ['chase', 'new Chase requests']]
      .filter(([key]) => activeSession.capabilities[key] === false)
      .map(([, label]) => label)
    : [];

  return (
    <div className="w-app" data-editor-session={editorSessionState} data-projection-source={projection && projection.source || undefined} data-projection-identity={projection && B.identity || undefined}>
      <header className={'w-top' + (topWidth < 1180 ? ' tight' : '') + (topWidth < 900 ? ' tighter' : '')} ref={topRef}>
        <span className="w-brand"><GooseMark size={24} /><b>Goose</b></span>
        <nav className="w-crumb" aria-label="Review navigation"><button type="button" className="w-back" onClick={back} aria-label="Back from review">Back</button><i>/</i><b title={job.name}>{job.name}</b></nav>
        <RwVariantIdentity jobId={job.id} session={reviewedRevision} isLive={isLive} />
        <span className={'w-verdict ' + verdictPresentation.tone} data-review-tone={verdictPresentation.tone} data-review-icon={verdictPresentation.icon} data-review-verdict={isLive ? revisionQa.state : job.verdict || 'CONDITIONAL'}><VerdictIcon size={13} /> {isLive ? revisionQa.label : job.verdict || 'CONDITIONAL'}</span>
        {job.status === 'complete' && job.verdict === 'PASS' && <span className="chip" data-review-reopened="1">Reopened — build previously approved</span>}
        <RwRevisionDetails session={reviewedRevision} capabilities={unavailableCapabilities} />
        <RwStatStrip items={stats} />
        <span className="w-grow" style={{ flex: 1 }}></span>
        <RwNotificationBell isLive={isLive} onOpenThread={openNotificationThread} />
        <button className={'btn btn-ghost btn-sm' + (layout.focus ? ' w-on' : '')} onClick={() => setLayout({ focus: !layout.focus })} title="Hide both side panels for a full-width render"><BI.eye size={14} /> {layout.focus ? 'Show panels' : 'Focus'}</button>
      </header>

      <div className="w-strips">   {/* RW-05 integration (🐓): status strips live in ONE always-present grid row — as direct .w-app children they stole the 1fr row and collapsed .w-body to 64px */}
        <RwWorkspaceNotices>
        {revisionHeadMoved && <div role="status" data-revision-status="moved" style={{ padding: '8px 14px', color: 'var(--rw-cond-ink)', background: 'var(--cond-bg)', fontSize: 12.5 }}><BI.warn size={14} style={{ verticalAlign: '-2px', marginRight: 6 }} />Saved revision changed — retain your draft, then reload to review the new head.</div>}
        {projectionReadOnly && <div role="status" data-projection-status="drift" data-projection-drift-reason={(sessionLoad.drift && sessionLoad.drift.reason) || 'unknown'} style={{ padding: '8px 14px', color: 'var(--cond)', background: 'var(--cond-bg)', fontSize: 12.5 }}><BI.warn size={14} style={{ verticalAlign: '-2px', marginRight: 6 }} />projection unavailable — {rwDriftCopy(sessionLoad.drift) || 'regenerating'}</div>}
        {sessionUnavailable && <div role="status" data-editor-session-status="unavailable" style={{ padding: '8px 14px', color: 'var(--cond)', background: 'var(--cond-bg)', fontSize: 12.5 }}><BI.warn size={14} style={{ verticalAlign: '-2px', marginRight: 6 }} />editor session unavailable — reload to review the exact revision</div>}
        {projectionMismatch && <div role="note" data-projection-parity="mismatch" style={{ padding: '7px 14px', color: 'var(--cond)', background: 'var(--cond-bg)', fontSize: 12 }}>projection identity differs — using the builder's</div>}
        {commentLinkNote && <div role="note" data-comment-link-note="missing-region" style={{ padding: '7px 14px', color: 'var(--rw-cond-ink)', background: 'var(--cond-bg)', fontSize: 12 }}>{commentLinkNote}</div>}
        </RwWorkspaceNotices>
      </div>

      <div className="w-body" data-context-restored={layout.forceCtx && fitted.showCtx ? 'true' : undefined} ref={bodyRef}>
        {fitted.showRail && <RwRail B={B} patchIds={new Set(chase.queue.map((patch) => patch.blockId))} mini={fitted.mini} width={fitted.rail} onHide={() => setLayout({ hideRail: true })} htmlState={htmlState} unboundAnchors={unboundAnchors} unmappedRegions={unmappedRegions} shellFinding={shellFinding} />}
        {fitted.showRail && <RwHandle side="blocks" onDrag={(delta) => setLayout({ rail: rwClamp(fitted.rail + delta, RW_LAYOUT.RAIL_MIN, Math.min(RW_LAYOUT.RAIL_MAX, railMax())) })} onReset={() => setLayout({ rail: RW_LAYOUT_DEFAULT.rail })} />}
        {!layout.focus && !fitted.showRail && <React.Fragment><div className="w-restore left"><button onClick={() => setLayout({ hideRail: false, forceCtx: false })} title={fitted.parkedRail ? 'Blocks — the window is too narrow for both panels and the canvas; opening it ' + (fitted.showCtx ? 'parks the review panel' : 'narrows the canvas') : 'Show the blocks panel'}><BI.layers size={15} /></button><span className="w-restore-lbl">blocks</span></div><span style={{ width: 14, flex: '0 0 14px' }}></span></React.Fragment>}

        <RwCanvas B={B} chase={chase} html={html} view={view} setView={setView} vp={vp} setVp={setVp} zoom={zoom} setZoom={setZoom} preview={preview} setPreview={setPreview} midWidth={midWidth} onFull={onFull} onViewDiff={onViewDiff} rwTabProps={rwTabProps} artifactUrl={frameArtifactUrl} isolated={isolated} builtRenderTries={builtRenderTries} builtReady={builtReady} setBuiltReady={setBuiltReady} htmlState={htmlState} />

        <RwContextColumn fitted={fitted} layout={layout} setLayout={setLayout} setTab={setTab}
          onDrag={(delta) => setLayout({ ctx: rwClamp(fitted.ctx - delta, RW_LAYOUT.CTX_MIN, Math.min(RW_LAYOUT.CTX_MAX, contextMax())) })}
          onReset={() => setLayout({ ctx: RW_LAYOUT_DEFAULT.ctx })}
          panel={<RwContextPanel B={B} chase={chase} tab={tab} setTab={setTab} onHide={() => setLayout({ hideCtx: true, forceCtx: false })} width={fitted.ctx} threads={threads} setThreads={setThreads} rwTabProps={rwTabProps} canInspect={canInspect} canAmend={canEdit} editBlockedCopy={editBlockedCopy} qaSummary={qaSummary} imgRefAudit={imgRefAudit} textAccuracy={textAccuracy} revisions={job.revisions || []} session={reviewedRevision} canRestore={canRestore} onRevisionHead={onRevisionHead} commentFeed={commentFeed} commentFocus={commentFocus} onCommentFocused={() => setCommentFocus(null)} />} />
      </div>

      <footer className="w-bar" data-amend-state={amend.state} data-draft-dirty={dirty ? 'true' : 'false'}>
        <div className="w-bar-status" aria-live="polite">
        {approvedReview ? <span className="w-ok w-review-result" role="status" data-review-result="approve"><BI.check size={15} /> Approved
          {approvedReview.flywheel && <span data-flywheel-receipt data-flywheel-mode={approvedReview.flywheel.mode} data-emission-status={approvedReview.flywheel.emission?.status}>{window.GooseReviewContributions.receiptLine(approvedReview.flywheel)}</span>}
        </span> : !isLive && <span className="w-bar-note" data-presend-status={allDone ? 'complete' : 'pending'}>Demo data · {allDone ? 'Pre-send complete' : pre.length - done + ' pre-send actions left'}</span>}
        <ReviewDeliveryControl jobId={job.id} revisionDigest={activeSession?.revision_digest}
          ready={reviewDeliveryReady({ isLive, currentReviewResult, job, session: activeSession, dirty,
            readOnly: workspaceReadOnly, pending: sessionPending || !!submitting || amendmentRunning })} />
        {revisionHeadMoved && draftDirty && <button data-action="download-retained-draft" onClick={() => {
          const url = URL.createObjectURL(new Blob([B.editor.dirty ? B.editor.html : html], { type: 'text/html;charset=utf-8' }));
          const link = document.createElement('a');link.href = url;link.download = 'unapproved-draft-' + String(job.id).replace(/[^\w-]/g, '_') + '.html';link.click();
          setTimeout(() => URL.revokeObjectURL(url), 1000);
        }}>Download unapproved HTML draft</button>}
        {B.editor.dirty && <span className="w-bar-note" data-code-draft-pending="1">{B.editor.stale ? 'Code draft base changed — copy your edits before reverting.' : 'Code edits are retained, not yet applied to the local draft.'}<button disabled={!canEdit || B.editor.stale} onClick={B.editor.apply}>Apply Code draft</button><button disabled={!canEdit} onClick={B.editor.discard}>Revert Code</button></span>}
        {!!chase.proposals.length && <span className="w-bar-note" data-chase-awaiting="1">{chase.proposals.length} Chase proposal{chase.proposals.length === 1 ? '' : 's'} awaiting Accept</span>}
        {replayPending
          ? <span className="w-bar-note" data-replay-status="pending">This revision is not yet re-verified — rerun QA before approval.</span>
          : dirty && <span className="w-bar-note">{(!activeSession || amendUnavailable || !(window.GooseAPI && window.GooseAPI.proposeAmendment))
            ? "The engine's artifact ships as certified; in-session edits are not persisted in this lane"   /* D6: honest in a lane without an editor session / amend capability (legacy, mock) */
            : derivedApplyCopy || 'Local draft — apply changes and rerun QA to certify it.'}</span>}
        {showCertify && <button className="btn btn-quiet btn-sm" data-action="certify-revision" disabled={!canCertify} title={draftDirty ? 'Apply the draft changes first' : revisionHeadMoved ? 'Reload the current revision first' : undefined} onClick={certifyRevision}>{amendmentRunning && amend.certifying ? 'Running QA…' : 'Run QA on this revision'}</button>}
        {certifyUnavailable && <span className="w-bar-note" data-qa-certify-unavailable>QA cannot be re-run on this saved revision — apply an edit to mint a measured revision.</span>}
        {replayPending && draftDirty && derivedApplyCopy && <span className="w-bar-note">{derivedApplyCopy}</span>}
        {amend.state === 'applied' && <span className="w-bar-note" role="status">Applied as {amend.revision} — Certified QA ({amend.revision})</span>}
        {amend.state === 'bundle_saved' && <span className="w-bar-note" role="status" data-bundle-result="saved">Bundle saved as {amend.revision} — {activeSession?.revision === amend.revision && !replayPending ? 'inspect the revision QA before approval' : 'rerun QA to re-verify'}</span>}
        {amend.state === 'certified' && <span className="w-bar-note" role="status">Certified QA ({amend.revision})</span>}
        {amend.state === 'noop' && <span className="w-bar-note" role="status">No change against the current head</span>}
        {!!(amend.stale && amend.stale.length) && <span className="w-bar-note" role="note" data-amend-stale="1">Additional QA checks are stale: {amend.stale.join(' · ')} — these checks were not rerun.</span>}
        {amend.error && <div role="alert" data-amend-error={amend.error.code} style={{ color: 'var(--fail)', fontSize: 12.5 }}>
          <b>{amend.error.title}</b>{amend.error.detail && <div>{amend.error.detail}</div>}
          {(amend.error.findings || amend.error.regressions || []).length > 0 && <ul>{(amend.error.findings || amend.error.regressions).map((item, index) => <li key={index}>{typeof item === 'string' ? item : JSON.stringify(item)}</li>)}</ul>}
          {amend.error.action === 'reload' && <button className="btn btn-quiet btn-sm" data-action="amend-reload" disabled={sessionRefreshing} onClick={() => {resetDrafts.current = { state: 'idle' };refreshSession('amend_reload');}}>Reload</button>}
          {amend.error.action === 'escalate' && amend.target && amend.target.scope === 'block' && <button className="btn btn-quiet btn-sm" data-action="amend-document" disabled={!canApply} onClick={() => applyChanges(true)}>Apply as a document-scope change</button>}
        </div>}
        {!shippable && <span className="w-bar-flag" data-receipt-flag={receipt?.status || 'loading'}><BI.warn size={13} /> {receiptLabel}{receipt !== undefined && ' · non-shippable'}</span>}
        {needVerdict && shippable && <span className="w-bar-flag" data-verdict-required><BI.review size={13} /> awaiting developer verdict</span>}
        {reviewError && !(gate && typeof reviewError === 'object') && <span className="w-review-error" role="alert" data-review-result="error" data-review-error={reviewError.code}>{typeof reviewError === 'object' ? [reviewError.title, reviewError.detail].filter(Boolean).join('\n') : reviewError}</span>}
        {currentReviewResult && currentReviewResult.decision !== 'approve' && <span className="w-ok" role="status" data-review-result={currentReviewResult.decision}><BI.check size={15} />{currentReviewResult.decision === 'reject' ? 'Rejection saved' : 'Changes requested'}</span>}
        </div>
        <RwReviewDecisions canReview={canReview} dirty={dirty} approvalWhy={approvalWhy} submitting={submitting} notesOpen={notesOpen}
          readOnlyReason={workspaceReadOnly ? projectionReadOnly ? 'Projection unavailable — reload before making a review decision.' : sessionUnavailable ? 'The exact editor session is unavailable — reload before making a review decision.' : 'The revision head changed — reload before making a review decision.' : null}
          onRequestChanges={() => {if (notesOpen) submitDecision('request_changes');else {setNotesOpen(true);setTab('review');setLayout({ hideCtx: false, focus: false, forceCtx: true });}}}
          onReject={() => submitDecision('reject')} onApprove={() => setGate(true)} />
        {dirty && roleCanEdit && !workspaceReadOnly && <div className="w-bar-edit-actions" role="group" aria-label="Apply or reset draft edits">
          <RwResetEditsButton disabled={!canResetEdits} html={html} codeHtml={B.editor.html}
            revisionKey={activeSession?.revision_digest} onReset={resetEdits} />
          <button className="btn btn-primary btn-lg" disabled={!canApply} title={!(window.GooseAPI && window.GooseAPI.proposeAmendment) ? 'Amendment replay is not wired in this lane — RWB-4' : !activeSession ? 'Apply needs the exact editor session — this lane has none' : derivedApplyCopy || undefined} onClick={() => applyChanges()} data-action="apply-rerun"><BI.retry size={15} /> {amendmentRunning && !amend.certifying ? 'Applying · rerunning QA…' : derivedApplyCopy ? 'Apply as a document-scope change' : 'Apply changes & rerun QA'}</button>
        </div>}
      </footer>

      {gate && canReview && !needVerdict && ReactDOM.createPortal(<ApproveSheet jobId={job.id} receipt={receipt} revision={reviewedRevision && reviewedRevision.revision} revisionDigest={approvalDigest} submitting={submitting === 'approve'} error={typeof reviewError === 'object' ? reviewError : null} onClose={() => {if (!submitting) {setGate(false);setReviewError(null);}}} onConfirm={(flywheel) => submitDecision('approve', flywheel)} />, document.body)}
      {full && canEdit && ReactDOM.createPortal(<RwFullCode B={B} chase={chase} html={html} isLive={isLive} job={job} artifactUrl={frameArtifactUrl} isolated={isolated} onClose={() => setFull(false)} />, document.body)}
      {feedback && <BuildFeedback job={job} go={go} onClose={() => setFeedback(false)} />}
    </div>
  );
}

Object.assign(window, { ReviewWorkspace, RwSessionRenderSlot });
